import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import Navigation from "../../components/Navigation/Navigation";
import Modal from "../../components/modal/index";
import { NotificationList } from "../../components/notification/NotificationList";
import { useNotification } from "../../components/notification/useNotification";
import { $apiV1, $apiV2 } from "../../config/api";
import {
  SavedFilterTemplatesControlsView,
} from "../../components/filterTemplates/SavedFilterTemplatesControls";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useLexicon } from "../../hooks/useLexicon";
import { useSavedFilterTemplates } from "../../hooks/useSavedFilterTemplates";
import {
  requestPushNotificationToken,
  subscribeToForegroundMessages,
} from "../../services/firebaseMessaging";
import { MainWrapper, MainWrapperFavorite } from "../../style/DasboardMain";
import {
  ActiveChip,
  ActiveChipRemove,
  ActiveChipRow,
  AdvancedParamEmptyState,
  AdvancedParamGrid,
  AdvancedParamGroup,
  AdvancedParamGroupBody,
  AdvancedParamGroupHeader,
  AdvancedParamGroupTitle,
  AdvancedParamInputs,
  AdvancedParamLabel,
  AdvancedParamRow,
  AdvancedBody,
  AdvancedToggle,
  AdvancedToggleIcon,
  CardActionItem,
  CardActionMenu,
  CardActionToggle,
  FilterDropdownActionButton,
  FilterDropdownChevron,
  FilterDropdownEmptyState,
  FilterDropdownOptionButton,
  FilterDropdownOptionCheck,
  FilterDropdownOptionText,
  FilterDropdownOptions,
  FilterDropdownPanel,
  FilterDropdownSearchInput,
  FilterDropdownTrigger,
  FilterDropdownTriggerValue,
  FilterDropdownWrap,
  FieldControl,
  FieldInput,
  FilterField,
  FilterLabel,
  FiltersGrid,
  LoadingState,
  PaginationButton,
  PaginationEllipsis,
  PaginationRow,
  PlayerAvatar,
  PlayerAvatarImage,
  PlayerCard,
  PlayerCardActions,
  PlayerCardDetails,
  PlayerCardIdentity,
  PlayerCardMeta,
  PlayerCardName,
  PlayerCardStat,
  PlayerCardStatLabel,
  PlayerCardStats,
  PlayerCardStatValue,
  PlayerCardTop,
  PlayerIdentityActionButton,
  PlayerIdentityActionGroup,
  PlayerIdentityNameLink,
  PlayerListInfo,
  PlayerListMeta,
  PlayerListName,
  PlayerListRow,
  PlayerListSmrt,
  PlayerListStats,
  PlayerMetaLink,
  PlayerMetaText,
  PositionBadge,
  RangeFieldRow,
  ResultsCount,
  ResultsGrid,
  ResultsHeader,
  ResultsList,
  ResultsSection,
  ResultsSortLabel,
  ResultsSortRow,
  ResultsSortSelect,
  SmrtIndexBarRow,
  SmrtIndexFill,
  SmrtIndexLabel,
  SmrtIndexTrack,
  SmrtIndexValue,
  SmrtSearchPanel,
  SmrtSearchTopBar,
  SmrtSearchWorkspace,
  TemplateControlsRow,
  TemplateHeaderTitle,
  TemplateToast,
  ViewToggleButton,
  ViewToggleGroup,
  WorkspaceEmptyState,
  WorkspaceSearchButton,
  WorkspaceSearchButtonRow,
  TutorialButton
} from "../../style/SmrtSearch";
import {
  PanelToggleButton,
} from "../../style/CompareWorkspaceStyle";
import emptySearch from "../../images/ic_empty.svg";
import starOn from "../../images/star_on.svg";
import starOff from "../../images/star_off.svg";
import compareIcon from "../../images/ic_compare.svg";
import shadowTeamIconDark from "../../images/ic_formation.svg";
import shadowTeamIconLight from "../../images/formation_blue.svg";
import { Tooltip } from "react-tooltip";
import {
  fetchShadowTeams,
  fetchShadowTeamById,
  addShadowTeamPlayer,
} from "../../store/shadowTeam/shadowTeamService";
import {
  trackCompareAction,
  trackCustomEvent,
  trackFavoriteChange,
  trackShadowTeamAction,
} from "../../tracking";
import {
  buildShadowTeamSlots,
  MAX_SHADOW_TEAM_PLAYERS_PER_SLOT,
  resolveShadowTeamAssignmentsBySlot,
} from "../shadowTeam/utils/formation";
import {
  RESULT_PAGE_SIZE,
  SORT_OPTIONS,
  STAT_MODES,
  VIEW_MODES,
  applyState,
  buildActiveChips,
  buildSearchRequestParams,
  captureState,
  clearChip,
  filtersToQuery,
  getPaginationItems,
  normalizeSearchSortKey,
  getSelectedAdvancedParamIds,
  getTemplateStateStatMode,
  hasVisibleFilters,
  hasSearchScopeFilters,
  loadAllPlayerSearchParameters,
  normalizeNextPageUrl,
  normalizeSearchStatMode,
  prepareAdvancedParamCatalog,
  queryToSearchState,
  sortSummaryResults,
  summarizeSearchResult,
} from "./utils";

const displayedParamsFielder = {
  "Mins Played": 1,
  "Passes +": 36,
  "Grave Mistakes": 162,
  "Goals +": 2,
  Assists: 201,
  "Passes +, %": 37,
  "Duels +, %": 124,
  Crosses: 50,
  "Aerial Duels +, %": 129,
  "Interceptions +": 153,
  "Ball Recoveries": 158,
  "Dribbles Made +, %": 114,
  "Tackles Made": 142,
  "Shots on Target, %": 26,
  "SmrtIndex League": 206,
  "SmrtIndex International": 207,
};

export const displayedParamsFielderIds = Object.values(displayedParamsFielder);

const displayedsParamsGK = {
  "Mins Played": 1,
  "Aerial Duels +, %": 129,
  "Effective Saves": 176,
  "Goals conceded": 173,
};

export const displayedsParamsGKIds = Object.values(displayedsParamsGK);

const TEAM_TYPE_OPTIONS = [
  { id: "1", labelKey: "smrtSearch.teamTypeClub" },
  { id: "2", labelKey: "smrtSearch.teamTypeNational" },
];

const QUICK_FILTER_OPTIONS = [
  { id: "GK", labelKey: "smrtSearch.quickFilterGk" },
  { id: "DEF", labelKey: "smrtSearch.quickFilterDef" },
  { id: "MID", labelKey: "smrtSearch.quickFilterMid" },
  { id: "FWD", labelKey: "smrtSearch.quickFilterFwd" },
  { id: "Left foot", labelKey: "smrtSearch.quickFilterLeftFoot" },
  { id: "Right foot", labelKey: "smrtSearch.quickFilterRightFoot" },
  { id: "Male", labelKey: "smrtSearch.quickFilterMale" },
  { id: "Female", labelKey: "smrtSearch.quickFilterFemale" },
];

const FEATURED_ADVANCED_FIELD_MAP = {
  xg: ["xgFrom", "xgTo"],
  npxg: ["npxgFrom", "npxgTo"],
  xa: ["xaFrom", "xaTo"],
  smrtindexleague: ["smrtIndexLeagueFrom", "smrtIndexLeagueTo"],
  smrtindexinternational: [
    "smrtIndexInternationalFrom",
    "smrtIndexInternationalTo",
  ],
  goals: ["goalsFrom", "goalsTo"],
};

const _isLightTheme = localStorage.getItem("theme") === "light";

const AVATAR_PALETTES = {
  GK: { background: "#1e2a1e", color: "#5dc87a" },
  DEF: { background: "#1a1e2a", color: "#6db3f5" },
  MID: { background: "#2a1e1e", color: "#f5a56d" },
  FWD: { background: "#1e1a2a", color: "#e87dd4" },
  DEFAULT: { background: _isLightTheme ? "#EFF4FF" : "#2a2a1a", color: _isLightTheme ? "#2363F7" : "#c8f500" },
};

const SMRT_SEARCH_TEMPLATE_PAGE_KEY = "players_search";
const SMART_SEARCH_RESULTS_ENDPOINT = "/platform/players_search_new";
const SMART_SEARCH_EXPORT_ENDPOINT = "/platform/players_search/export/pdf";
const SMART_SEARCH_EXPORT_ASYNC_ENDPOINT =
  "/platform/players_search/export/pdf/async";
const SMRT_SEARCH_PDF_READY_EVENT_TYPE = "smrt_search_pdf_ready";
const EXPORT_SCOPE_OPTIONS = {
  CURRENT: "current",
  ALL: "all",
};
const SMART_SEARCH_EXPORT_COLUMNS = [
  "name",
  "club",
  "nation",
  "age",
  "position",
  "goals",
  "assists",
  "xg",
  "smrt_index",
];
const MAX_ALL_PAGES_EXPORT_COUNT = 100;
const COMPETITION_PAGE_SIZE = 50;
const SEASON_PAGE_SIZE = 40;
const INITIAL_SEASON_VISIBLE_COUNT = 5;
const DEFAULT_SEASON_PAGE = 1;
const SMRT_SEARCH_TUTORIAL_STORAGE_KEY = "smrt_search_tutorial_seen_v1";
const SMRT_SEARCH_TUTORIAL_ACCENT = _isLightTheme ? "#2363F7" : "#BEFF00";
const SMRT_SEARCH_TUTORIAL_CARD_BG = _isLightTheme ? "#ffffff" : "linear-gradient(145deg, #1a2612, #131c0e)";
const SMRT_SEARCH_TUTORIAL_TITLE_COLOR = _isLightTheme ? "#0f1117" : "#ffffff";
const SMRT_SEARCH_TUTORIAL_BODY_COLOR = _isLightTheme ? "#6B7280" : "#8a9e80";
const SMRT_SEARCH_TUTORIAL_SKIP_COLOR = _isLightTheme ? "#9CA3AF" : "#5a6e50";
const SMRT_SEARCH_TUTORIAL_BTN_TEXT = _isLightTheme ? "#ffffff" : "#0a0e05";
const SMRT_SEARCH_TUTORIAL_STEP_INACTIVE = _isLightTheme ? "#E5E7EB" : "#ffffff22";
const SMRT_SEARCH_TUTORIAL_FONT_BASE =
  '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const SMRT_SEARCH_TUTORIAL_FONT_MEDIUM =
  '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const SMRT_SEARCH_TUTORIAL_FONT_BOLD =
  '"Satoshi Medium", "Satoshi-Regular", sans-serif';

const SHADOW_TEAM_BAND_LABELS = {
  gk: "Goalkeeper",
  defense: "Defense",
  defensiveMid: "Defensive Mid",
  midfield: "Midfield",
  attackingMid: "Attacking Mid",
  attack: "Attack",
};

const ShadowTeamModalShell = styled.div`
  width: min(100%, 520px);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ShadowTeamModalTitle = styled.h2`
  margin: 0;
  color: ${(props) => props.theme.white};
  font-family: "SatoshiBold";
  font-size: 24px;
  line-height: 1.2;
  text-align: left;
`;

const ShadowTeamModalSubtitle = styled.p`
  margin: 0;
  color: ${(props) => props.theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
`;

const ShadowTeamModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(56vh, 520px);
  overflow-y: auto;
  padding-right: 4px;
`;

const ShadowTeamModalBack = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${(props) => props.theme.textMuted};
  font-family: "SatoshiMedium";
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.white};
  }
`;

const ShadowTeamModalStepTitle = styled.p`
  margin: 0;
  color: ${(props) => props.theme.textSecondary};
  font-family: "SatoshiBold";
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: left;
`;

const ShadowTeamModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ShadowTeamModalItemButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border: 0.5px solid ${(props) => props.theme.borderColor};
  border-radius: 10px;
  background: ${(props) => props.theme.cardBg};
  color: ${(props) => props.theme.white};
  font-family: "SatoshiMedium";
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${(props) => props.theme.green};
    background: ${(props) => props.theme.accentBg};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }
`;

const ShadowTeamModalItemText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ShadowTeamModalItemTitle = styled.span`
  color: ${(props) => props.theme.white};
  font-family: "SatoshiBold";
  font-size: 13px;
  line-height: 1.3;
`;

const ShadowTeamModalItemMeta = styled.span`
  color: ${(props) => props.theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 11px;
  line-height: 1.35;
`;

const ShadowTeamModalEmptyState = styled.div`
  padding: 16px 14px;
  border: 1px dashed ${(props) => props.theme.borderColor};
  border-radius: 12px;
  color: ${(props) => props.theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
`;

const ShadowTeamSlotSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ShadowTeamSlotSectionLabel = styled.span`
  color: ${(props) => props.theme.textMuted};
  font-family: "SatoshiMedium";
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: left;
`;

const ShadowTeamSlotGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ShadowTeamSlotButton = styled.button`
  min-width: 54px;
  padding: 7px 12px;
  border: 0.5px solid ${(props) => props.theme.borderColor};
  border-radius: 999px;
  background: ${(props) => props.theme.inputBg};
  color: ${(props) => props.theme.textSecondary};
  font-family: "SatoshiBold";
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${(props) => props.theme.green};
    color: ${(props) => props.theme.green};
    background: ${(props) => props.theme.accentBg};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const getShadowTeamSlotGroups = (formation) => {
  if (!Array.isArray(formation?.lines) || formation.lines.length === 0) {
    return [];
  }

  const slots = buildShadowTeamSlots(formation.lines);
  const byLine = {};

  slots.forEach((slot) => {
    const key = slot.lineIndex;
    if (!byLine[key]) {
      byLine[key] = { lineIndex: key, band: slot.band, slots: [] };
    }
    byLine[key].slots.push(slot);
  });

  return Object.values(byLine).sort((a, b) => {
    if (a.lineIndex === -1) return 1;
    if (b.lineIndex === -1) return -1;
    return b.lineIndex - a.lineIndex;
  });
};

let cachedFilterOptions = null;
let filterOptionsPromise = null;
let cachedAdvancedParams = null;
let advancedParamsPromise = null;

const GENDER_OPTIONS = [
  { id: "M", labelKey: "smrtSearch.quickFilterMale" },
  { id: "F", labelKey: "smrtSearch.quickFilterFemale" },
];

const LEG_OPTIONS = [
  { id: "L", labelKey: "smrtSearch.quickFilterLeftFoot" },
  { id: "R", labelKey: "smrtSearch.quickFilterRightFoot" },
  { id: "RL", labelKey: "smrtSearch.bothOption" },
];

const getOptionId = (option) => String(option?.id ?? "");

const getOptionDisplayLabel = (option, fallback = "") =>
  String(option?.label || option?.name || option?.years || fallback || "");

const POSITION_SHORT_CODE_PATTERN = /^[A-Z0-9]{2,6}$/;

const normalizePositionDescriptor = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const includesAnyPositionToken = (text, tokens = []) =>
  tokens.some((token) => text.includes(token));

const resolvePositionRoleCode = (descriptor = "") => {
  if (includesAnyPositionToken(descriptor, ["goalkeeper", "goal keeper"])) {
    return "GK";
  }

  if (includesAnyPositionToken(descriptor, ["attacking midfielder"])) {
    return "AM";
  }

  if (
    includesAnyPositionToken(descriptor, [
      "defensive midfielder",
      "holding midfielder",
    ])
  ) {
    return "DM";
  }

  if (includesAnyPositionToken(descriptor, ["midfielder", "midfield"])) {
    return "M";
  }

  if (
    includesAnyPositionToken(descriptor, [
      "defender",
      "center back",
      "centre back",
      "center-back",
      "centre-back",
      "fullback",
      "wingback",
      "wing back",
      "back",
    ])
  ) {
    return "D";
  }

  if (
    includesAnyPositionToken(descriptor, [
      "forward",
      "attacker",
      "striker",
      "winger",
    ])
  ) {
    return "F";
  }

  return "";
};

const resolvePositionSideCode = (descriptor = "") => {
  if (includesAnyPositionToken(descriptor, ["left central", "left centre"])) {
    return "LC";
  }

  if (includesAnyPositionToken(descriptor, ["right central", "right centre"])) {
    return "RC";
  }

  if (includesAnyPositionToken(descriptor, ["central", "center", "centre"])) {
    return "C";
  }

  if (descriptor.includes("left")) {
    return "L";
  }

  if (descriptor.includes("right")) {
    return "R";
  }

  return "";
};

const derivePositionCodeFromDescriptor = (value = "") => {
  const descriptor = normalizePositionDescriptor(value);

  if (!descriptor) {
    return "";
  }

  const roleCode = resolvePositionRoleCode(descriptor);

  if (!roleCode) {
    return "";
  }

  if (roleCode === "GK") {
    return "GK";
  }

  const sideCode = resolvePositionSideCode(descriptor);

  if (sideCode) {
    return `${sideCode}${roleCode}`;
  }

  if (roleCode === "M") {
    return "MID";
  }

  if (roleCode === "D") {
    return "DEF";
  }

  if (roleCode === "F") {
    return "FWD";
  }

  return roleCode;
};

const getPositionShortCode = (position = {}, lexiconLabel = "") => {
  const trustedCandidates = [
    position?.short_name,
    position?.shortName,
    position?.code,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const trustedCodeCandidate = trustedCandidates.find((value) =>
    POSITION_SHORT_CODE_PATTERN.test(value.toUpperCase())
  );

  if (trustedCodeCandidate) {
    return trustedCodeCandidate.toUpperCase();
  }

  const derivedCodeCandidate = [position?.name, position?.label, lexiconLabel]
    .map((value) => derivePositionCodeFromDescriptor(value))
    .find(Boolean);

  if (derivedCodeCandidate) {
    return derivedCodeCandidate;
  }

  const fallbackCandidates = [position?.name, position?.label, lexiconLabel]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const fallbackCodeCandidate = fallbackCandidates.find(
    (value) =>
      value === value.toUpperCase() &&
    POSITION_SHORT_CODE_PATTERN.test(value.toUpperCase())
  );

  return fallbackCodeCandidate ? fallbackCodeCandidate.toUpperCase() : "";
};

const getSeasonOptionLabel = (option, fallback = "") =>
  String(option?.years || option?.label || option?.name || fallback || "");

const getSeasonOptionLabelScore = (season) => {
  const label = getSeasonOptionLabel(season);
  const resolvedYears = getSeasonYears(season);

  return (
    label.length + (season?.years ? 10 : 0) + (resolvedYears.length > 1 ? 20 : 0)
  );
};

const getSeasonOptionDedupKey = (season) => {
  const resolvedYears = getSeasonYears(season);

  if (resolvedYears.length) {
    return resolvedYears.join("-");
  }

  return getSeasonOptionLabel(season).trim().toLowerCase();
};

const mergeOptionsById = (existingOptions = [], incomingOptions = []) => {
  const nextOptionsMap = new Map();

  [...existingOptions, ...incomingOptions].forEach((option) => {
    const optionId = getOptionId(option);

    if (!optionId) {
      return;
    }

    nextOptionsMap.set(optionId, option);
  });

  return Array.from(nextOptionsMap.values());
};

const buildOrderedOptions = (
  selectedIds = [],
  visibleOptions = [],
  catalogOptions = visibleOptions
) => {
  const selectedOptionMap = new Map(
    mergeOptionsById(catalogOptions, visibleOptions).map((option) => [
      getOptionId(option),
      option,
    ])
  );
  const selectedOptionIds = new Set(selectedIds.map(String));
  const selectedOptions = selectedIds
    .map((id) => selectedOptionMap.get(String(id)))
    .filter(Boolean);
  const remainingOptions = visibleOptions.filter(
    (option) => !selectedOptionIds.has(getOptionId(option))
  );

  return mergeOptionsById(selectedOptions, remainingOptions);
};

const dedupeVisibleSeasonOptions = (seasonOptions = []) => {
  const preferredOptionsByKey = new Map();

  seasonOptions.forEach((season) => {
    const dedupKey = getSeasonOptionDedupKey(season);

    if (!dedupKey) {
      return;
    }

    const existingOption = preferredOptionsByKey.get(dedupKey);

    if (
      !existingOption ||
      getSeasonOptionLabelScore(season) > getSeasonOptionLabelScore(existingOption)
    ) {
      preferredOptionsByKey.set(dedupKey, season);
    }
  });

  return Array.from(preferredOptionsByKey.values());
};

const sortSeasonOptionsByRecency = (seasonOptions = []) =>
  [...seasonOptions].sort((leftSeason, rightSeason) => {
    const leftYears = getSeasonYears(leftSeason);
    const rightYears = getSeasonYears(rightSeason);
    const leftNewestYear = leftYears.length ? Math.max(...leftYears) : Number.NEGATIVE_INFINITY;
    const rightNewestYear = rightYears.length
      ? Math.max(...rightYears)
      : Number.NEGATIVE_INFINITY;

    if (leftNewestYear !== rightNewestYear) {
      return rightNewestYear - leftNewestYear;
    }

    const leftOldestYear = leftYears.length ? Math.min(...leftYears) : Number.NEGATIVE_INFINITY;
    const rightOldestYear = rightYears.length
      ? Math.min(...rightYears)
      : Number.NEGATIVE_INFINITY;

    if (leftOldestYear !== rightOldestYear) {
      return rightOldestYear - leftOldestYear;
    }

    return getSeasonOptionLabel(rightSeason).localeCompare(
      getSeasonOptionLabel(leftSeason),
      undefined,
      {
        numeric: true,
        sensitivity: "base",
      }
    );
  });

const buildVisibleSeasonOptions = (seasonOptions = []) =>
  sortSeasonOptionsByRecency(dedupeVisibleSeasonOptions(seasonOptions));

const getHasMoreResults = (payload, pageSize, loadedCount) => {
  const totalCount = Number(payload?.count);

  if (Number.isFinite(totalCount) && totalCount >= 0) {
    return loadedCount < totalCount;
  }

  if (payload?.next) {
    return true;
  }

  return loadedCount > 0 && loadedCount % pageSize === 0;
};

const getSeasonHasMoreResults = ({
  payload,
  search = "",
  page = 1,
  loadedCount = 0,
}) => {
  const normalizedSearch = String(search || "").trim();

  if (!normalizedSearch && page >= DEFAULT_SEASON_PAGE) {
    if (payload?.next) {
      return true;
    }

    const pageResults = Array.isArray(payload?.results) ? payload.results : [];
    return pageResults.length >= SEASON_PAGE_SIZE;
  }

  return getHasMoreResults(payload, SEASON_PAGE_SIZE, loadedCount);
};

const loadMissingIndexedOptions = async ({
  endpoint,
  missingIds = [],
  initialParams = {},
}) => {
  if (!missingIds.length) {
    return [];
  }

  const unresolvedIds = new Set(missingIds.map(String));
  const resolvedOptions = [];
  const buildNextRequest = (url) => {
    if (!url) {
      return null;
    }

    try {
      const parsedUrl = new URL(url, "https://smrtstats.local");
      const nextParams = Object.fromEntries(
        Array.from(parsedUrl.searchParams.entries()).map(([key, value]) => [
          key,
          /^\d+$/.test(value) ? Number(value) : value,
        ])
      );

      return {
        url: parsedUrl.pathname,
        config: Object.keys(nextParams).length
          ? {
              params: nextParams,
            }
          : undefined,
      };
    } catch {
      return {
        url,
        config: undefined,
      };
    }
  };
  let nextRequest = {
    url: endpoint,
    config: {
      params: initialParams,
    },
  };

  while (nextRequest && unresolvedIds.size) {
    const response = await $apiV1.get(nextRequest.url, nextRequest.config);
    const payload = response?.data || {};
    const pageResults = Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload)
        ? payload
        : [];

    pageResults.forEach((option) => {
      const optionId = getOptionId(option);

      if (!optionId || !unresolvedIds.has(optionId)) {
        return;
      }

      unresolvedIds.delete(optionId);
      resolvedOptions.push(option);
    });

    const nextPageUrl = payload?.next ? normalizeNextPageUrl(payload.next) : null;

    nextRequest = buildNextRequest(nextPageUrl);
  }

  return resolvedOptions;
};

const normalizeSeasonYearPart = (part, referenceYear = null) => {
  const digits = String(part || "").match(/\d{2,4}/)?.[0];

  if (!digits) {
    return Number.NaN;
  }

  const numericValue = Number(digits);

  if (digits.length === 4 || !Number.isFinite(referenceYear)) {
    return numericValue;
  }

  const centuryBase = Math.floor(referenceYear / 100) * 100;
  const candidateYear = centuryBase + numericValue;

  return candidateYear < referenceYear ? candidateYear + 100 : candidateYear;
};

const getSeasonYears = (season) => {
  const seasonLabel = getSeasonOptionLabel(season);
  const rawYearParts = seasonLabel.match(/\d{2,4}/g) || [];
  const resolvedYears = [];

  rawYearParts.forEach((part, index) => {
    const previousYear =
      index > 0 ? resolvedYears[resolvedYears.length - 1] : null;
    const resolvedYear = normalizeSeasonYearPart(part, previousYear);

    if (Number.isFinite(resolvedYear)) {
      resolvedYears.push(resolvedYear);
    }
  });

  return resolvedYears;
};

const hasSeasonStarted = (
  season,
  currentYear = new Date().getFullYear()
) => {
  const resolvedYears = getSeasonYears(season);

  if (!resolvedYears.length) {
    return true;
  }

  return Math.min(...resolvedYears) <= currentYear;
};

const filterStartedSeasons = (
  seasonOptions = [],
  currentYear = new Date().getFullYear()
) => seasonOptions.filter((season) => hasSeasonStarted(season, currentYear));

const loadSmrtSearchFilterOptions = async () => {
  if (cachedFilterOptions) {
    return cachedFilterOptions;
  }

  if (!filterOptionsPromise) {
    filterOptionsPromise = Promise.allSettled([
      $apiV1.get("/platform/position", {
        params: { no_pagination: true },
      }),
      $apiV1.get("/platform/competition", {
        params: {
          page: 1,
          page_size: COMPETITION_PAGE_SIZE,
          sort: "alphabet",
        },
      }),
      $apiV1.get("/platform/year_season", {
        params: {
          page: DEFAULT_SEASON_PAGE,
          per_page: SEASON_PAGE_SIZE,
        },
      }),
      $apiV1.get("/platform/nationality", {
        params: {
          with_tournaments: true,
          page_size: 300,
        },
      }),
    ]).then((requests) => {
      const [positionsResult, competitionsResult, seasonsResult, nationsResult] =
        requests;
      const competitionPayload =
        competitionsResult.status === "fulfilled"
          ? competitionsResult.value?.data
          : null;
      const seasonPayload =
        seasonsResult.status === "fulfilled" ? seasonsResult.value?.data : null;
      const competitionResults = Array.isArray(competitionPayload?.results)
        ? competitionPayload.results
        : [];
      const rawSeasonResults = Array.isArray(seasonPayload?.results)
        ? seasonPayload.results
        : [];
      const seasonResults = filterStartedSeasons(rawSeasonResults);

      const nextFilterOptions = {
        positions:
          positionsResult.status === "fulfilled" &&
          Array.isArray(positionsResult.value?.data)
            ? positionsResult.value.data.filter((item) => item.id !== 23)
            : [],
        competitions: competitionResults,
        competitionsHasMore: getHasMoreResults(
          competitionPayload,
          COMPETITION_PAGE_SIZE,
          competitionResults.length
        ),
        seasons: seasonResults,
        seasonsHasMore: getSeasonHasMoreResults({
          payload: seasonPayload,
          search: "",
          page: DEFAULT_SEASON_PAGE,
          loadedCount: rawSeasonResults.length,
        }),
        seasonsRawLoadedCount: rawSeasonResults.length,
        nations:
          nationsResult.status === "fulfilled"
            ? nationsResult.value?.data?.results || []
            : [],
        hasError: requests.some((result) => result.status === "rejected"),
      };

      cachedFilterOptions = nextFilterOptions;
      return nextFilterOptions;
    });
  }

  return filterOptionsPromise;
};

const loadSmrtSearchAdvancedParams = async () => {
  if (cachedAdvancedParams) {
    return cachedAdvancedParams;
  }

  if (!advancedParamsPromise) {
    advancedParamsPromise = loadAllPlayerSearchParameters((nextPage) =>
      $apiV1.get(nextPage)
    )
      .then((parameters) => {
        cachedAdvancedParams = parameters;
        return parameters;
      })
      .catch((error) => {
        advancedParamsPromise = null;
        throw error;
      });
  }

  return advancedParamsPromise;
};

export const resetSmrtSearchRequestCachesForTests = () => {
  cachedFilterOptions = null;
  filterOptionsPromise = null;
  cachedAdvancedParams = null;
  advancedParamsPromise = null;
};

const getAvatarPalette = (positionCode) =>
  AVATAR_PALETTES[positionCode] || AVATAR_PALETTES.DEFAULT;

const formatDecimal = (value) => Number(value || 0).toFixed(2);

const formatInteger = (value) => String(Math.round(Number(value || 0)));

const formatOptionalInteger = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? String(Math.round(numericValue)) : "";
};

const formatFlexibleStatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  if (Number.isInteger(numericValue)) {
    return String(numericValue);
  }

  return numericValue.toFixed(2).replace(/\.?0+$/, "");
};

const normalizeErrorMessage = (error, fallbackMessage) => {
  const responseMessage = error?.response?.data?.message;

  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage;
  }

  return fallbackMessage;
};

const resolveSmrtSearchPdfReadyPayload = (payload = {}) => {
  const data = payload?.data || {};
  const notification = payload?.notification || {};

  return {
    type: data.type || payload?.type || "",
    jobId: data.job_id || payload?.job_id || "",
    downloadUrl:
      data.download_url ||
      data.downloadUrl ||
      payload?.download_url ||
      payload?.downloadUrl ||
      payload?.fcmOptions?.link ||
      notification?.click_action ||
      "",
    body: notification.body || data.body || payload?.body || "",
  };
};

const isSmrtSearchPdfReadyEvent = (readyPayload = {}) => {
  const normalizedType = String(readyPayload.type || "").toLowerCase();

  if (!readyPayload.downloadUrl) {
    return false;
  }

  if (!normalizedType) {
    return true;
  }

  return (
    normalizedType === SMRT_SEARCH_PDF_READY_EVENT_TYPE ||
    normalizedType === "smrtsearchpdfready" ||
    normalizedType === "pdf_ready"
  );
};

const openNewTab = (path) => {
  window.open(path, "_blank");
};

const buildRectSnapshot = (rect) => ({
  left: rect.left,
  top: rect.top,
  width: rect.width,
  height: rect.height,
  right: rect.right,
  bottom: rect.bottom,
});

const mergeElementRects = (elements) => {
  const rects = elements
    .filter(Boolean)
    .map((element) => element.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0);

  if (!rects.length) {
    return null;
  }

  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.right));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));

  return buildRectSnapshot({
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  });
};

const buildSmrtSearchTutorialSteps = (t, { includeResultsStep = false } = {}) => {
  const baseSteps = [
    {
      id: "welcome",
      spotlight: null,
      title: t("smrtSearch.tutorialWelcomeTitle"),
      body: t("smrtSearch.tutorialWelcomeBody"),
      position: "center",
    },
    {
      id: "templates",
      spotlight: "templateControls",
      title: t("smrtSearch.tutorialTemplatesTitle"),
      body: t("smrtSearch.tutorialTemplatesBody"),
      position: "below",
    },
    {
      id: "filters",
      spotlight: "filters",
      title: t("smrtSearch.tutorialFiltersTitle"),
      body: t("smrtSearch.tutorialFiltersBody"),
      position: "below",
    },
    {
      id: "advanced",
      spotlight: "advanced",
      title: t("smrtSearch.tutorialAdvancedTitle"),
      body: t("smrtSearch.tutorialAdvancedBody"),
      position: "below",
    },
    {
      id: "search",
      spotlight: "search",
      title: t("smrtSearch.tutorialSearchTitle"),
      body: t("smrtSearch.tutorialSearchBody"),
      position: "above",
    },
  ];

  if (includeResultsStep) {
    baseSteps.push({
      id: "results",
      spotlight: "results",
      title: t("smrtSearch.tutorialResultsTitle"),
      body: t("smrtSearch.tutorialResultsBody"),
      position: "below",
    });
    baseSteps.push({
      id: "resultsActions",
      spotlight: null,
      title: t("smrtSearch.tutorialResultsActionsTitle"),
      body: t("smrtSearch.tutorialResultsActionsBody"),
      position: "center",
    });
  }

  return baseSteps;
};

const resolvePlayerPhotoUrl = (photo) => {
  if (!photo) {
    return "";
  }

  if (/^https?:\/\//i.test(photo)) {
    return photo;
  }

  const normalizedPhoto = String(photo).replace(/^\/+/, "");
  const currentDomain = window.location.hostname;
  const isLocalhost =
    currentDomain.includes("localhost") || currentDomain.includes("127.0.0.1");

  return isLocalhost
    ? `https://football.smrtstats.com/${normalizedPhoto}`
    : normalizedPhoto;
};

const filterObjectFromEmptyValues = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    )
  );

const requestSmrtSearchResults = async (params) => {
  try {
    return await $apiV2.get(SMART_SEARCH_RESULTS_ENDPOINT, {
      params,
    });
  } catch (error) {
    const shouldRetryWithoutSort =
      params?.sort && Number(error?.response?.status) >= 500;

    if (!shouldRetryWithoutSort) {
      throw error;
    }

    const { sort: ignoredSort, ...fallbackParams } = params;

    // Some server-side sorts fail for valid filter combinations, so retry
    // without the sort and rely on the existing page-level client sorting.
    return $apiV2.get(SMART_SEARCH_RESULTS_ENDPOINT, {
      params: fallbackParams,
    });
  }
};

const PlayerActionsMenu = ({
  onAddToShadowTeam,
  favorite,
  onCompare,
  onOpenProfile,
  onToggleFavorite,
  player,
  t,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useOutsideClick(() => {
    if (open) {
      setOpen(false);
    }
  });

  return (
    <PlayerCardActions ref={menuRef} style={style}>
      <CardActionToggle
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((previousOpen) => !previousOpen);
        }}
        aria-label={t("smrtSearch.moreActions")}
      >
        ...
      </CardActionToggle>

      {open ? (
        <CardActionMenu>
          <CardActionItem
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onOpenProfile(player);
            }}
          >
            {t("smrtSearch.profileAction")}
          </CardActionItem>
          <CardActionItem
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onCompare(player);
            }}
          >
            {t("smrtSearch.comparePlayerAction")}
          </CardActionItem>
          <CardActionItem
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onAddToShadowTeam(player);
            }}
          >
            {t("smrtSearch.shadowTeamPlayerAction")}
          </CardActionItem>
          <CardActionItem
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onToggleFavorite(player);
            }}
          >
            {favorite
              ? t("compareWorkspace.removeFavorite")
              : t("compareWorkspace.addFavorite")}
          </CardActionItem>
        </CardActionMenu>
      ) : null}
    </PlayerCardActions>
  );
};

const DropdownField = ({
  id,
  label,
  isOpen,
  onClose,
  onToggle,
  summary,
  placeholder,
  children,
}) => {
  const dropdownRef = useOutsideClick(() => {
    if (isOpen) {
      onClose();
    }
  });

  return (
    <FilterField>
      <FilterLabel as="span">{label}</FilterLabel>
      <FilterDropdownWrap ref={dropdownRef}>
        <FilterDropdownTrigger
          id={id}
          type="button"
          aria-expanded={isOpen}
          aria-label={label}
          onClick={onToggle}
          $open={isOpen}
        >
          <FilterDropdownTriggerValue $placeholder={!summary}>
            {summary || placeholder}
          </FilterDropdownTriggerValue>
          <FilterDropdownChevron $open={isOpen}>▾</FilterDropdownChevron>
        </FilterDropdownTrigger>
        {isOpen ? <FilterDropdownPanel>{children}</FilterDropdownPanel> : null}
      </FilterDropdownWrap>
    </FilterField>
  );
};

const buildTeamPath = (clubId) => `/tournaments/team/${clubId}`;
const buildPlayerPath = (playerId) => `/tournaments/player/${playerId}`;

const PlayerIdentityAvatar = ({ player, compact = false }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const avatarPalette = getAvatarPalette(player.positionCode);
  const photoUrl =
    player.photo && !imageFailed ? resolvePlayerPhotoUrl(player.photo) : "";

  if (photoUrl) {
    return (
      <PlayerAvatarImage
        $compact={compact}
        src={photoUrl}
        alt={player.fullName}
        onError={() => {
          setImageFailed(true);
        }}
      />
    );
  }

  return (
    <PlayerAvatar
      $compact={compact}
      $background={avatarPalette.background}
      $color={avatarPalette.color}
    >
      {player.initials}
    </PlayerAvatar>
  );
};

const PlayerMetaSummary = ({ player, ageLabel = "" }) => {
  const items = [];

  if (player.clubName) {
    items.push(
      player.clubId ? (
        <PlayerMetaLink
          key="club"
          href={buildTeamPath(player.clubId)}
          target="_blank"
          rel="noreferrer"
        >
          {player.clubName}
        </PlayerMetaLink>
      ) : (
        <PlayerMetaText key="club">{player.clubName}</PlayerMetaText>
      )
    );
  }

  if (player.nationName) {
    items.push(<PlayerMetaText key="nation">{player.nationName}</PlayerMetaText>);
  }

  if (Number.isFinite(player.age) && ageLabel) {
    items.push(
      <PlayerMetaText key="age">{`${ageLabel} ${player.age}`}</PlayerMetaText>
    );
  }

  if (!items.length) {
    return null;
  }

  return items.map((item, index) => (
    <React.Fragment key={`meta-${index}`}>
      {index > 0 ? <PlayerMetaText aria-hidden="true"> · </PlayerMetaText> : null}
      {item}
    </React.Fragment>
  ));
};

const Index = () => {
  const { t, i18n } = useTranslation();
  const l = useLexicon();
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, addNotification, removeNotification } = useNotification();
  const dispatch = useDispatch();
  const shadowTeams = useSelector((state) => state.shadowTeam.teams);
  const shadowTeamLoadingTeams = useSelector(
    (state) => state.shadowTeam.loading.teams
  );
  const shadowTeamAddingPlayer = useSelector(
    (state) => state.shadowTeam.loading.addPlayer
  );

  const initialSearchStateRef = useRef(queryToSearchState(location.search));
  const initialSearchState = initialSearchStateRef.current;
  const initialHasSearchScopeFilters = hasSearchScopeFilters(
    initialSearchState.filters
  );
  const initialSearchExecutedRef = useRef(false);
  const requestIdRef = useRef(0);
  const filterOptionsLoadedRef = useRef(false);
  const advancedParamsLoadedRef = useRef(false);
  const addNotificationRef = useRef(addNotification);
  const translateRef = useRef(t);
  const competitionAbortControllerRef = useRef(null);
  const competitionOptionsRef = useRef([]);
  const competitionCatalogRef = useRef([]);
  const seasonOptionsRef = useRef([]);
  const seasonCatalogRef = useRef([]);
  const seasonRawLoadedCountRef = useRef(0);
  const defaultCompetitionOptionsRef = useRef([]);
  const defaultSeasonOptionsRef = useRef([]);
  const defaultCompetitionHasMoreRef = useRef(false);
  const defaultSeasonHasMoreRef = useRef(false);
  const defaultSeasonRawLoadedCountRef = useRef(0);
  const competitionListRef = useRef(null);
  const competitionLoadMoreRef = useRef(null);
  const templateControlsRef = useRef(null);
  const filtersGridRef = useRef(null);
  const advancedToggleRef = useRef(null);
  const searchActionsRef = useRef(null);
  const resultsHeaderRef = useRef(null);
  const tutorialLaunchResolvedRef = useRef(false);

  const [filters, setFilters] = useState(initialSearchState.filters);
  const [page, setPage] = useState(initialSearchState.page);
  const [sortKey, setSortKey] = useState(initialSearchState.sort);
  const [appliedAdvancedParamRanges, setAppliedAdvancedParamRanges] = useState(
    {}
  );
  const [viewMode, setViewMode] = useState(VIEW_MODES.LIST);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [globalAdvancedModeChoice, setGlobalAdvancedModeChoice] = useState("");
  const [hasSearched, setHasSearched] = useState(initialHasSearchScopeFilters);

  const [positions, setPositions] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [competitionOptions, setCompetitionOptions] = useState([]);
  const [competitionSearchTerm, setCompetitionSearchTerm] = useState("");
  const [competitionPage, setCompetitionPage] = useState(1);
  const [competitionHasMore, setCompetitionHasMore] = useState(false);
  const [competitionOpen, setCompetitionOpen] = useState(false);
  const [competitionLoading, setCompetitionLoading] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [seasonSearchTerm, setSeasonSearchTerm] = useState("");
  const [seasonPage, setSeasonPage] = useState(DEFAULT_SEASON_PAGE);
  const [seasonHasMore, setSeasonHasMore] = useState(false);
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [visibleSeasonCount, setVisibleSeasonCount] = useState(
    INITIAL_SEASON_VISIBLE_COUNT
  );
  const [nations, setNations] = useState([]);
  const [nationalitySearchTerm, setNationalitySearchTerm] = useState("");
  const [nationalityOpen, setNationalityOpen] = useState(false);
  const [positionSearchTerm, setPositionSearchTerm] = useState("");
  const [positionOpen, setPositionOpen] = useState(false);
  const [legOpen, setLegOpen] = useState(false);
  const [genderOpen, setGenderOpen] = useState(false);
  const [teamTypeOpen, setTeamTypeOpen] = useState(false);
  const [rawAdvancedParams, setRawAdvancedParams] = useState([]);
  const [loadingAdvancedParams, setLoadingAdvancedParams] = useState(false);
  const [manualAdvancedGroups, setManualAdvancedGroups] = useState({});

  const [rawPlayers, setRawPlayers] = useState([]);
  const [resultAdvancedParamIds, setResultAdvancedParamIds] = useState([]);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportScope, setExportScope] = useState("");

  const [favoritePlayers, setFavoritePlayers] = useState({});
  const [shadowTeamModalPlayer, setShadowTeamModalPlayer] = useState(null);
  const [shadowTeamSelectedTeamId, setShadowTeamSelectedTeamId] = useState(null);
  const [shadowTeamLoadingTeamId, setShadowTeamLoadingTeamId] = useState(null);
  const [templateStatusMessage, setTemplateStatusMessage] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [navigationOpen, setNavigationOpen] = useState(
    localStorage.getItem("navigationOpen")
  );
  const [initialFiltersResolved, setInitialFiltersResolved] = useState(false);
  const [initialAdvancedParamsResolved, setInitialAdvancedParamsResolved] =
    useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);

  const resolveAdvancedParamLabel = useCallback(
    (parameter) => {
      const lexiconLabel = parameter?.lexic_id ? l(parameter.lexic_id) : "";
      return lexiconLabel || parameter?.name || parameter?.parameter_name || "";
    },
    [l]
  );

  const advancedParamCatalog = useMemo(
    () => prepareAdvancedParamCatalog(rawAdvancedParams, resolveAdvancedParamLabel),
    [rawAdvancedParams, resolveAdvancedParamLabel]
  );

  const shadowTeamModalOpen = Boolean(shadowTeamModalPlayer);
  const shadowTeamModalPlayerName = useMemo(
    () =>
      shadowTeamModalPlayer?.known_name ||
      shadowTeamModalPlayer?.fullName ||
      shadowTeamModalPlayer?.name ||
      "",
    [shadowTeamModalPlayer]
  );
  const selectedShadowTeam = useMemo(() => {
    const normalizedSelectedTeamId = Number(shadowTeamSelectedTeamId);

    if (!Number.isFinite(normalizedSelectedTeamId)) {
      return null;
    }

    return (
      shadowTeams.find((team) => Number(team?.id) === normalizedSelectedTeamId) ||
      null
    );
  }, [shadowTeamSelectedTeamId, shadowTeams]);
  const selectedShadowTeamHasLoadedAssignments = Array.isArray(
    selectedShadowTeam?.assignments
  );
  const isShadowTeamDetailLoading =
    shadowTeamLoadingTeamId !== null &&
    Number(shadowTeamLoadingTeamId) === Number(shadowTeamSelectedTeamId);
  const selectedShadowTeamSlotPlayers = useMemo(() => {
    if (!selectedShadowTeamHasLoadedAssignments) {
      return {};
    }

    return resolveShadowTeamAssignmentsBySlot(
      selectedShadowTeam?.assignments || [],
      selectedShadowTeam?.formation?.lines || []
    );
  }, [selectedShadowTeam, selectedShadowTeamHasLoadedAssignments]);
  const isShadowTeamSlotFull = useCallback(
    (slot) =>
      (selectedShadowTeamSlotPlayers?.[slot?.key] || []).length >=
      MAX_SHADOW_TEAM_PLAYERS_PER_SLOT,
    [selectedShadowTeamSlotPlayers]
  );

  const advancedParamLabelMap = useMemo(
    () =>
      Object.values(advancedParamCatalog.itemsById).reduce((acc, item) => {
        acc[String(item.id)] = item.label;
        return acc;
      }, {}),
    [advancedParamCatalog.itemsById]
  );

  const teamTypeMap = useMemo(() => {
    return TEAM_TYPE_OPTIONS.reduce((acc, option) => {
      acc[option.id] = t(option.labelKey);
      return acc;
    }, {});
  }, [t]);

  const genderLabelMap = useMemo(() => {
    return GENDER_OPTIONS.reduce((acc, option) => {
      acc[option.id] = t(option.labelKey);
      return acc;
    }, {});
  }, [t]);

  const legLabelMap = useMemo(() => {
    return LEG_OPTIONS.reduce((acc, option) => {
      acc[option.id] = t(option.labelKey);
      return acc;
    }, {});
  }, [t]);

  const statModeOptions = useMemo(
    () => [
      {
        value: STAT_MODES.SEASON,
        label: t("compareWorkspace.perSeason"),
      },
      {
        value: STAT_MODES.MATCH,
        label: t("compareWorkspace.perMatch"),
      },
      {
        value: STAT_MODES.NINETY,
        label: t("compareWorkspace.per90"),
      },
    ],
    [t]
  );

  const getPositionOptionLabel = useCallback(
    (position) => {
      const lexiconLabel = position?.lexic_id ? l(position.lexic_id) : "";
      const shortCode = getPositionShortCode(position, lexiconLabel);
      return shortCode || lexiconLabel || getOptionDisplayLabel(position);
    },
    [l]
  );

  const positionLabelMap = useMemo(
    () =>
      positions.reduce((acc, position) => {
        acc[getOptionId(position)] = getPositionOptionLabel(position);
        return acc;
      }, {}),
    [getPositionOptionLabel, positions]
  );

  const competitionLabelMap = useMemo(
    () =>
      competitions.reduce((acc, competition) => {
        acc[getOptionId(competition)] = getOptionDisplayLabel(competition);
        return acc;
      }, {}),
    [competitions]
  );

  const seasonLabelMap = useMemo(
    () =>
      seasons.reduce((acc, season) => {
        acc[getOptionId(season)] = getSeasonOptionLabel(season);
        return acc;
      }, {}),
    [seasons]
  );

  const nationLabelMap = useMemo(
    () =>
      nations.reduce((acc, nation) => {
        acc[getOptionId(nation)] = getOptionDisplayLabel(nation);
        return acc;
      }, {}),
    [nations]
  );

  const getSelectedLabels = useCallback((selectedIds, labelMap) => {
    return selectedIds
      .map((id) => labelMap[String(id)] || String(id))
      .filter(Boolean);
  }, []);

  const filterChipLabels = useMemo(
    () => ({
      competition: t("compareWorkspace.filterCompetition"),
      season: t("compareWorkspace.filterSeason"),
      nationality: t("smrtSearch.nationalityLabel"),
      position: t("smrtSearch.position"),
      leg: t("smrtSearch.legLabel"),
      both: t("smrtSearch.bothOption"),
      age: t("compareWorkspace.filterAge"),
      height: t("compareWorkspace.filterHeight"),
      teamType: t("compareWorkspace.filterTeamType"),
      xg: t("compareWorkspace.filterXg"),
      npxg: t("compareWorkspace.filterNpxg"),
      xa: t("compareWorkspace.filterXa"),
      smrtIndexLeague: t("compareWorkspace.filterSmrtIndexLeague"),
      smrtIndexInternational: t("compareWorkspace.filterSmrtIndexInternational"),
      goals: t("compareWorkspace.filterGoals"),
    }),
    [t]
  );

  const quickFilterLabelMap = useMemo(() => {
    return QUICK_FILTER_OPTIONS.reduce((acc, option) => {
      acc[option.id] = t(option.labelKey);
      return acc;
    }, {});
  }, [t]);

  const activeChips = useMemo(() => {
    return buildActiveChips(
      filters,
      {
        positions,
        competitions,
        seasons,
        nations,
        teamTypes: teamTypeMap,
        advancedParamLabels: advancedParamLabelMap,
      },
      filterChipLabels
    );
  }, [
    advancedParamLabelMap,
    competitions,
    filterChipLabels,
    filters,
    nations,
    positions,
    seasons,
    teamTypeMap,
  ]);

  const selectedCompetitionLabels = useMemo(
    () => getSelectedLabels(filters.selectedCompetition, competitionLabelMap),
    [competitionLabelMap, filters.selectedCompetition, getSelectedLabels]
  );

  const selectedSeasonLabels = useMemo(
    () => getSelectedLabels(filters.selectedSeason, seasonLabelMap),
    [filters.selectedSeason, getSelectedLabels, seasonLabelMap]
  );

  const selectedNationLabels = useMemo(
    () => getSelectedLabels(filters.selectedNation, nationLabelMap),
    [filters.selectedNation, getSelectedLabels, nationLabelMap]
  );

  const selectedPositionLabels = useMemo(
    () => getSelectedLabels(filters.selectedPosition, positionLabelMap),
    [filters.selectedPosition, getSelectedLabels, positionLabelMap]
  );

  const selectedTeamTypeLabel = teamTypeMap[filters.selectedTeamType] || "";
  const selectedLegLabel = legLabelMap[filters.selectedLeg] || "";
  const selectedGenderLabel = genderLabelMap[filters.selectedGender] || "";

  const orderedCompetitionOptions = useMemo(
    () =>
      buildOrderedOptions(
        filters.selectedCompetition,
        competitionOptions,
        competitions
      ),
    [competitionOptions, competitions, filters.selectedCompetition]
  );

  const orderedSeasonOptions = useMemo(
    () =>
      buildVisibleSeasonOptions(
        buildOrderedOptions(filters.selectedSeason, seasonOptions, seasons)
      ),
    [filters.selectedSeason, seasonOptions, seasons]
  );
  const displayedSeasonOptions = useMemo(
    () => orderedSeasonOptions.slice(0, visibleSeasonCount),
    [orderedSeasonOptions, visibleSeasonCount]
  );
  const canRevealMoreSeasonOptions =
    orderedSeasonOptions.length > visibleSeasonCount;

  const orderedNationOptions = useMemo(() => {
    const normalizedSearch = nationalitySearchTerm.trim().toLowerCase();
    const visibleOptions = nations.filter((nation) => {
      const nationLabel = getOptionDisplayLabel(nation).toLowerCase();
      return !normalizedSearch || nationLabel.includes(normalizedSearch);
    });

    return buildOrderedOptions(filters.selectedNation, visibleOptions, nations);
  }, [filters.selectedNation, nations, nationalitySearchTerm]);

  const orderedPositionOptions = useMemo(() => {
    const normalizedSearch = positionSearchTerm.trim().toLowerCase();
    const visibleOptions = positions.filter((position) => {
      const positionLabel = getPositionOptionLabel(position).toLowerCase();
      return !normalizedSearch || positionLabel.includes(normalizedSearch);
    });

    return buildOrderedOptions(filters.selectedPosition, visibleOptions, positions);
  }, [
    filters.selectedPosition,
    getPositionOptionLabel,
    positionSearchTerm,
    positions,
  ]);

  const summarizedPlayers = useMemo(() => {
    return sortSummaryResults(
      rawPlayers.map((player) =>
        summarizeSearchResult(player, {
          selectedParamIds: resultAdvancedParamIds,
          paramLabels: advancedParamLabelMap,
          advancedParamRanges: appliedAdvancedParamRanges,
        })
      ),
      sortKey
    );
  }, [
    advancedParamLabelMap,
    appliedAdvancedParamRanges,
    rawPlayers,
    resultAdvancedParamIds,
    sortKey,
  ]);
  const smrtSearchTutorialSteps = useMemo(
    () =>
      buildSmrtSearchTutorialSteps(t, {
        includeResultsStep: summarizedPlayers.length > 0,
      }),
    [summarizedPlayers.length, t]
  );

  const totalPages = Math.max(1, Math.ceil(totalPlayers / RESULT_PAGE_SIZE));
  const canExportPdf =
    summarizedPlayers.length > 0 && !loadingSearch && !isExportingPdf;
  const paginationItems = useMemo(
    () => getPaginationItems(page, totalPages),
    [page, totalPages]
  );

  useEffect(() => {
    addNotificationRef.current = addNotification;
    translateRef.current = t;
  }, [addNotification, t]);

  useEffect(() => {
    let isActive = true;
    let unsubscribeFromMessages = () => {};

    const initForegroundMessaging = async () => {
      const unsubscribe = await subscribeToForegroundMessages((payload) => {
        const readyPayload = resolveSmrtSearchPdfReadyPayload(payload);

        if (!isSmrtSearchPdfReadyEvent(readyPayload)) {
          return;
        }

        addNotificationRef.current(
          readyPayload.body || translateRef.current("smrtSearch.exportPdfReady"),
          "success",
          {
            id: readyPayload.jobId
              ? `smrt-search-export-${readyPayload.jobId}`
              : Date.now(),
            actionLabel: readyPayload.downloadUrl
              ? translateRef.current("smrtSearch.exportPdfDownloadAction")
              : "",
            actionUrl: readyPayload.downloadUrl || "",
          }
        );
      });

      if (!isActive) {
        if (typeof unsubscribe === "function") {
          unsubscribe();
        }
        return;
      }

      if (typeof unsubscribe === "function") {
        unsubscribeFromMessages = unsubscribe;
      }
    };

    void initForegroundMessaging();

    return () => {
      isActive = false;
      unsubscribeFromMessages();
    };
  }, []);

  useEffect(() => {
    competitionOptionsRef.current = competitionOptions;
  }, [competitionOptions]);

  useEffect(() => {
    competitionCatalogRef.current = competitions;
  }, [competitions]);

  useEffect(() => {
    seasonOptionsRef.current = seasonOptions;
  }, [seasonOptions]);

  useEffect(() => {
    seasonCatalogRef.current = seasons;
  }, [seasons]);

  useEffect(() => {
    const handleNavigationOpenChange = () => {
      setNavigationOpen(localStorage.getItem("navigationOpen"));
    };

    window.addEventListener("navigationOpenChange", handleNavigationOpenChange);

    return () => {
      window.removeEventListener(
        "navigationOpenChange",
        handleNavigationOpenChange
      );
    };
  }, []);

  useEffect(() => {
    if (filterOptionsLoadedRef.current) {
      return;
    }

    filterOptionsLoadedRef.current = true;
    let isMounted = true;

    const loadFilterOptions = async () => {
      try {
        const nextFilterOptions = await loadSmrtSearchFilterOptions();

        if (!isMounted) {
          return;
        }

        setPositions(nextFilterOptions.positions);
        setCompetitions(nextFilterOptions.competitions);
        setCompetitionOptions(nextFilterOptions.competitions);
        setCompetitionPage(1);
        setCompetitionHasMore(nextFilterOptions.competitionsHasMore);
        setSeasons(nextFilterOptions.seasons);
        setSeasonOptions(buildVisibleSeasonOptions(nextFilterOptions.seasons));
        setSeasonPage(DEFAULT_SEASON_PAGE);
        setSeasonHasMore(nextFilterOptions.seasonsHasMore);
        seasonRawLoadedCountRef.current =
          nextFilterOptions.seasonsRawLoadedCount || 0;
        setNations(nextFilterOptions.nations);
        defaultCompetitionOptionsRef.current = nextFilterOptions.competitions;
        defaultSeasonOptionsRef.current = buildVisibleSeasonOptions(
          nextFilterOptions.seasons
        );
        defaultCompetitionHasMoreRef.current =
          nextFilterOptions.competitionsHasMore;
        defaultSeasonHasMoreRef.current = nextFilterOptions.seasonsHasMore;
        defaultSeasonRawLoadedCountRef.current =
          nextFilterOptions.seasonsRawLoadedCount || 0;

        if (nextFilterOptions.hasError) {
          addNotificationRef.current(
            translateRef.current("smrtSearch.loadFiltersError"),
            "error"
          );
        }
      } catch (error) {
        if (isMounted) {
          addNotificationRef.current(
            normalizeErrorMessage(
              error,
              translateRef.current("smrtSearch.loadFiltersError")
            ),
            "error"
          );
        }
      } finally {
        if (isMounted) {
          setInitialFiltersResolved(true);
        }
      }
    };

    loadFilterOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (advancedParamsLoadedRef.current) {
      return;
    }

    advancedParamsLoadedRef.current = true;

    let isMounted = true;

    const loadAdvancedParameterCatalog = async () => {
      setLoadingAdvancedParams(true);

      try {
        const nextParameters = await loadSmrtSearchAdvancedParams();

        if (isMounted) {
          setRawAdvancedParams(nextParameters);
        }
      } catch (error) {
        if (isMounted) {
          addNotificationRef.current(
            translateRef.current("smrtSearch.loadFiltersError"),
            "error"
          );
        }
      } finally {
        if (isMounted) {
          setLoadingAdvancedParams(false);
          setInitialAdvancedParamsResolved(true);
        }
      }
    };

    loadAdvancedParameterCatalog();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (competitionAbortControllerRef.current) {
        competitionAbortControllerRef.current.abort();
      }
    };
  }, []);

  const startSmrtSearchTutorial = useCallback(() => {
    setTutorialStep(0);
    setTutorialRect(null);
    setShowTutorial(true);
  }, []);

  const completeSmrtSearchTutorial = useCallback(() => {
    try {
      localStorage.setItem(SMRT_SEARCH_TUTORIAL_STORAGE_KEY, "1");
    } catch {
      // noop
    }

    setShowTutorial(false);
    setTutorialRect(null);
  }, []);

  const handleTutorialNext = useCallback(() => {
    if (tutorialStep >= smrtSearchTutorialSteps.length - 1) {
      completeSmrtSearchTutorial();
      return;
    }

    setTutorialStep((previousStep) => previousStep + 1);
  }, [completeSmrtSearchTutorial, smrtSearchTutorialSteps.length, tutorialStep]);

  const handleTutorialSkip = useCallback(() => {
    completeSmrtSearchTutorial();
  }, [completeSmrtSearchTutorial]);

  const handleTutorialReplay = useCallback(() => {
    startSmrtSearchTutorial();
  }, [startSmrtSearchTutorial]);

  useEffect(() => {
    if (tutorialLaunchResolvedRef.current) {
      return;
    }

    if (!initialFiltersResolved || !initialAdvancedParamsResolved) {
      return;
    }

    tutorialLaunchResolvedRef.current = true;

    try {
      if (localStorage.getItem(SMRT_SEARCH_TUTORIAL_STORAGE_KEY) !== "1") {
        startSmrtSearchTutorial();
      }
    } catch {
      startSmrtSearchTutorial();
    }
  }, [
    initialAdvancedParamsResolved,
    initialFiltersResolved,
    startSmrtSearchTutorial,
  ]);

  useEffect(() => {
    if (!showTutorial) {
      return undefined;
    }

    const currentStep = smrtSearchTutorialSteps[tutorialStep];

    if (!currentStep?.spotlight) {
      setTutorialRect(null);
      return undefined;
    }

    const updateRect = () => {
      let nextRect = null;

      switch (currentStep.spotlight) {
        case "templateControls":
          nextRect = mergeElementRects([templateControlsRef.current]);
          break;
        case "filters":
          nextRect = mergeElementRects([filtersGridRef.current]);
          break;
        case "advanced":
          nextRect = mergeElementRects([advancedToggleRef.current]);
          break;
        case "search":
          nextRect = mergeElementRects([searchActionsRef.current]);
          break;
        case "results":
          nextRect = mergeElementRects([resultsHeaderRef.current]);
          break;
        default:
          nextRect = null;
      }

      setTutorialRect(nextRect);
    };

    const timeoutId = window.setTimeout(updateRect, 70);
    const animationFrameId =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(updateRect)
        : null;

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      window.clearTimeout(timeoutId);
      if (
        animationFrameId !== null &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [
    advancedOpen,
    loadingSearch,
    navigationOpen,
    showTutorial,
    smrtSearchTutorialSteps,
    summarizedPlayers.length,
    tutorialStep,
  ]);

  const toggleMultiFilterValue = useCallback((filterKey, value) => {
    const normalizedValue = String(value);

    setFilters((previousFilters) => {
      const previousValues = Array.isArray(previousFilters[filterKey])
        ? previousFilters[filterKey].map(String)
        : [];
      const isSelected = previousValues.includes(normalizedValue);

      return {
        ...previousFilters,
        [filterKey]: isSelected
          ? previousValues.filter((item) => item !== normalizedValue)
          : [...previousValues, normalizedValue],
      };
    });
  }, []);

  const setSingleFilterValue = useCallback((filterKey, nextValue) => {
    const normalizedValue = String(nextValue || "");

    setFilters((previousFilters) => ({
      ...previousFilters,
      [filterKey]:
        normalizedValue && previousFilters[filterKey] === normalizedValue
          ? ""
          : normalizedValue,
    }));
  }, []);

  const setSelectedNationValue = useCallback((nextValue) => {
    const normalizedValue = String(nextValue || "");

    setFilters((previousFilters) => ({
      ...previousFilters,
      selectedNation: normalizedValue ? [normalizedValue] : [],
    }));
  }, []);

  const requestCompetitionOptions = useCallback(
    async ({ search = "", page: nextPage = 1, append = false } = {}) => {
      if (competitionAbortControllerRef.current) {
        competitionAbortControllerRef.current.abort();
      }

      const abortController =
        typeof AbortController === "function" ? new AbortController() : null;

      competitionAbortControllerRef.current = abortController;
      setCompetitionLoading(true);

      try {
        const response = await $apiV1.get("/platform/competition", {
          params: {
            page: nextPage,
            page_size: COMPETITION_PAGE_SIZE,
            search,
            sort: "alphabet",
          },
          ...(abortController ? { signal: abortController.signal } : {}),
        });
        const payload = response?.data || {};
        const nextResults = Array.isArray(payload?.results) ? payload.results : [];
        const nextVisibleOptions = append
          ? mergeOptionsById(competitionOptionsRef.current, nextResults)
          : nextResults;
        const nextCatalogOptions = mergeOptionsById(
          competitionCatalogRef.current,
          nextResults
        );

        setCompetitionOptions(nextVisibleOptions);
        setCompetitions(nextCatalogOptions);
        setCompetitionPage(nextPage);
        setCompetitionHasMore(
          getHasMoreResults(
            payload,
            COMPETITION_PAGE_SIZE,
            nextVisibleOptions.length
          )
        );
      } catch (error) {
        if (
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError" ||
          abortController?.signal?.aborted
        ) {
          return;
        }

        addNotificationRef.current(
          normalizeErrorMessage(
            error,
            translateRef.current("smrtSearch.loadFiltersError")
          ),
          "error"
        );
      } finally {
        if (!abortController || !abortController.signal.aborted) {
          setCompetitionLoading(false);
        }
      }
    },
    []
  );

  const requestSeasonOptions = useCallback(
    async ({ search = "", page: nextPage = 1, append = false } = {}) => {
      setSeasonLoading(true);

      try {
        const response = await $apiV1.get("/platform/year_season", {
          params: {
            page: nextPage,
            per_page: SEASON_PAGE_SIZE,
            search,
          },
        });
        const payload = response?.data || {};
        const nextResults = Array.isArray(payload?.results) ? payload.results : [];
        const filteredNextResults = filterStartedSeasons(nextResults);
        const nextVisibleOptions = append
          ? mergeOptionsById(seasonOptionsRef.current, filteredNextResults)
          : filteredNextResults;
        const nextCatalogOptions = mergeOptionsById(
          seasonCatalogRef.current,
          filteredNextResults
        );
        const nextRawLoadedCount = append
          ? seasonRawLoadedCountRef.current + nextResults.length
          : nextResults.length;

        seasonRawLoadedCountRef.current = nextRawLoadedCount;

        setSeasonOptions(buildVisibleSeasonOptions(nextVisibleOptions));
        setSeasons(nextCatalogOptions);
        setSeasonPage(nextPage);
        setSeasonHasMore(
          getSeasonHasMoreResults({
            payload,
            search,
            page: nextPage,
            loadedCount: nextRawLoadedCount,
          })
        );
      } catch (error) {
        addNotificationRef.current(
          normalizeErrorMessage(
            error,
            translateRef.current("smrtSearch.loadFiltersError")
          ),
          "error"
        );
      } finally {
        setSeasonLoading(false);
      }
    },
    []
  );

  const handleCompetitionLoadMore = useCallback(() => {
    if (!competitionOpen || competitionLoading || !competitionHasMore) {
      return;
    }

    requestCompetitionOptions({
      search: competitionSearchTerm.trim(),
      page: competitionPage + 1,
      append: true,
    });
  }, [
    competitionHasMore,
    competitionLoading,
    competitionOpen,
    competitionPage,
    competitionSearchTerm,
    requestCompetitionOptions,
  ]);

  const handleSeasonLoadMore = useCallback(() => {
    if (!seasonOpen || seasonLoading) {
      return;
    }

    setVisibleSeasonCount(
      (previousCount) => previousCount + INITIAL_SEASON_VISIBLE_COUNT
    );

    if (canRevealMoreSeasonOptions || !seasonHasMore) {
      return;
    }

    requestSeasonOptions({
      search: seasonSearchTerm.trim(),
      page: seasonPage + 1,
      append: true,
    });
  }, [
    canRevealMoreSeasonOptions,
    requestSeasonOptions,
    seasonHasMore,
    seasonLoading,
    seasonOpen,
    seasonPage,
    seasonSearchTerm,
  ]);

  useInfiniteScroll({
    wrapperRef: competitionListRef,
    triggerRef: competitionLoadMoreRef,
    callback: handleCompetitionLoadMore,
  });

  useEffect(() => {
    if (!competitionOpen) {
      return;
    }

    const normalizedSearch = competitionSearchTerm.trim();

    if (!normalizedSearch) {
      setCompetitionOptions(defaultCompetitionOptionsRef.current);
      setCompetitionPage(1);
      setCompetitionHasMore(defaultCompetitionHasMoreRef.current);

      if (!defaultCompetitionOptionsRef.current.length) {
        requestCompetitionOptions({
          search: "",
          page: 1,
          append: false,
        });
      }

      return;
    }

    requestCompetitionOptions({
      search: normalizedSearch,
      page: 1,
      append: false,
    });
  }, [competitionOpen, competitionSearchTerm, requestCompetitionOptions]);

  useEffect(() => {
    if (!seasonOpen) {
      return;
    }

    const normalizedSearch = seasonSearchTerm.trim();

    setVisibleSeasonCount(INITIAL_SEASON_VISIBLE_COUNT);

    if (!normalizedSearch) {
      setSeasonOptions(defaultSeasonOptionsRef.current);
      setSeasonPage(DEFAULT_SEASON_PAGE);
      setSeasonHasMore(defaultSeasonHasMoreRef.current);
      seasonRawLoadedCountRef.current = defaultSeasonRawLoadedCountRef.current;

      if (!defaultSeasonOptionsRef.current.length) {
        requestSeasonOptions({
          search: "",
          page: DEFAULT_SEASON_PAGE,
          append: false,
        });
      }

      return;
    }

    requestSeasonOptions({
      search: normalizedSearch,
      page: 1,
      append: false,
    });
  }, [requestSeasonOptions, seasonOpen, seasonSearchTerm]);

  useEffect(() => {
    const missingCompetitionIds = filters.selectedCompetition.filter(
      (id) => !competitionLabelMap[String(id)]
    );

    if (!missingCompetitionIds.length) {
      return undefined;
    }

    let isMounted = true;

    const loadMissingCompetitions = async () => {
      let resolvedOptions = [];

      try {
        resolvedOptions = await loadMissingIndexedOptions({
          endpoint: "/platform/competition",
          missingIds: missingCompetitionIds,
          initialParams: {
            page: 1,
            page_size: COMPETITION_PAGE_SIZE,
            sort: "alphabet",
          },
        });
      } catch {
        return;
      }

      if (!isMounted) {
        return;
      }

      if (!resolvedOptions.length) {
        return;
      }

      setCompetitions((previousOptions) =>
        mergeOptionsById(previousOptions, resolvedOptions)
      );
    };

    loadMissingCompetitions();

    return () => {
      isMounted = false;
    };
  }, [competitionLabelMap, filters.selectedCompetition]);

  useEffect(() => {
    const missingSeasonIds = filters.selectedSeason.filter(
      (id) => !seasonLabelMap[String(id)]
    );

    if (!missingSeasonIds.length) {
      return undefined;
    }

    let isMounted = true;

    const loadMissingSeasons = async () => {
      let resolvedOptions = [];

      try {
        resolvedOptions = await loadMissingIndexedOptions({
          endpoint: "/platform/year_season",
          missingIds: missingSeasonIds,
          initialParams: {
            page: 1,
            per_page: SEASON_PAGE_SIZE,
          },
        });
      } catch {
        return;
      }

      if (!isMounted) {
        return;
      }

      if (!resolvedOptions.length) {
        return;
      }

      setSeasons((previousOptions) =>
        mergeOptionsById(previousOptions, resolvedOptions)
      );
    };

    loadMissingSeasons();

    return () => {
      isMounted = false;
    };
  }, [filters.selectedSeason, seasonLabelMap]);

  const applyLegacyModeToActiveRows = useCallback(
    (currentFilters, legacyModeValue) => {
      const normalizedLegacyMode = normalizeSearchStatMode(legacyModeValue);

      if (normalizedLegacyMode === STAT_MODES.SEASON) {
        return currentFilters;
      }

      const previousRanges = currentFilters.advancedParamRanges || {};
      const nextRanges = {
        ...previousRanges,
      };
      let hasUpdates = false;

      advancedParamCatalog.items.forEach((parameter) => {
        const paramId = String(parameter.id);
        const staticFieldKeys = FEATURED_ADVANCED_FIELD_MAP[parameter.featuredKey];
        const existingRange = nextRanges[paramId];

        if (existingRange?.mode) {
          return;
        }

        if (staticFieldKeys) {
          const [fromField, toField] = staticFieldKeys;
          const hasStaticRange = Boolean(
            currentFilters[fromField] || currentFilters[toField]
          );

          if (!hasStaticRange) {
            return;
          }

          nextRanges[paramId] = {
            ...(existingRange || {}),
            mode: normalizedLegacyMode,
          };
          hasUpdates = true;
          return;
        }

        const hasDynamicRange = Boolean(existingRange?.from || existingRange?.to);

        if (!hasDynamicRange) {
          return;
        }

        nextRanges[paramId] = {
          ...existingRange,
          mode: normalizedLegacyMode,
        };
        hasUpdates = true;
      });

      if (!hasUpdates) {
        return currentFilters;
      }

      return {
        ...currentFilters,
        advancedParamRanges: nextRanges,
      };
    },
    [advancedParamCatalog.items]
  );

  const buildFeaturedParamModes = useCallback(
    (nextFilters) =>
      advancedParamCatalog.items.reduce((acc, parameter) => {
        const staticFieldKeys = FEATURED_ADVANCED_FIELD_MAP[parameter.featuredKey];

        if (!staticFieldKeys) {
          return acc;
        }

        const [fromField, toField] = staticFieldKeys;
        const hasStaticRange = Boolean(nextFilters[fromField] || nextFilters[toField]);

        if (!hasStaticRange) {
          return acc;
        }

        const currentMode = normalizeSearchStatMode(
          nextFilters.advancedParamRanges?.[String(parameter.id)]?.mode
        );

        acc[String(parameter.id)] = currentMode;
        return acc;
      }, {}),
    [advancedParamCatalog.items]
  );

  const syncUrl = useCallback(
    (nextFilters, nextPage, nextSort) => {
      const nextQuery = filtersToQuery({
        filters: nextFilters,
        page: nextPage,
        sort: nextSort,
      });

      navigate(
        {
          pathname: location.pathname,
          search: nextQuery ? `?${nextQuery}` : "",
        },
        { replace: true }
      );
    },
    [location.pathname, navigate]
  );

  const executeSearch = useCallback(
    async ({
      nextFilters = filters,
      nextPage = page,
      nextSort = sortKey,
      sync = true,
      revealResults = true,
    } = {}) => {
      const currentRequestId = requestIdRef.current + 1;
      requestIdRef.current = currentRequestId;

      const nextFeaturedParamModes = buildFeaturedParamModes(nextFilters);

      if (sync) {
        syncUrl(nextFilters, nextPage, nextSort);
      }

      if (revealResults) {
        setHasSearched(true);
      }

      setLoadingSearch(true);

      try {
        const searchRequestParams = filterObjectFromEmptyValues(
          buildSearchRequestParams({
            filters: nextFilters,
            page: nextPage,
            sort: nextSort,
            additionalParamModes: nextFeaturedParamModes,
          })
        );
        const response = await requestSmrtSearchResults(searchRequestParams);

        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        setRawPlayers(Array.isArray(response?.data?.results) ? response.data.results : []);
        setResultAdvancedParamIds(
          getSelectedAdvancedParamIds(nextFilters.advancedParamRanges)
        );
        setTotalPlayers(Number(response?.data?.count || 0));
        setAppliedAdvancedParamRanges(nextFilters.advancedParamRanges || {});
        trackCustomEvent({
          action: "search",
          entityType: "search_result",
          eventType: "smrt_search_executed",
          metadata: {
            filters: searchRequestParams,
            page: nextPage,
            results_count: Number(response?.data?.count || 0),
            sort: nextSort,
          },
          sourceSurface: "smrt_search",
        });
      } catch (error) {
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        addNotification(
          normalizeErrorMessage(error, t("smrtSearch.searchError")),
          "error"
        );
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setLoadingSearch(false);
        }
      }
    },
    [
      addNotification,
      buildFeaturedParamModes,
      filters,
      page,
      sortKey,
      syncUrl,
      t,
    ]
  );

  useEffect(() => {
    if (!initialHasSearchScopeFilters) {
      return;
    }

    if (!initialAdvancedParamsResolved) {
      return;
    }

    if (initialSearchExecutedRef.current) {
      return;
    }

    initialSearchExecutedRef.current = true;
    const nextInitialFilters = applyLegacyModeToActiveRows(
      initialSearchState.filters,
      initialSearchState.statMode
    );

    setFilters(nextInitialFilters);

    executeSearch({
      nextFilters: nextInitialFilters,
      nextPage: initialSearchState.page,
      nextSort: initialSearchState.sort,
      sync: false,
      revealResults: true,
    });
  }, [
    applyLegacyModeToActiveRows,
    executeSearch,
    initialHasSearchScopeFilters,
    initialAdvancedParamsResolved,
    initialSearchState.filters,
    initialSearchState.page,
    initialSearchState.sort,
    initialSearchState.statMode,
  ]);

  const showToast = useCallback((message) => {
    setToastMessage(message);
  }, []);

  useEffect(() => {
    if (!toastMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setToastMessage("");
    }, 2200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [toastMessage]);

  const buildExportRows = useCallback((players) => {
    return players.map((player) => ({
      name: player.known_name || player.fullName,
      club: player.clubName || "",
      nation: player.nationName || "",
      age: formatOptionalInteger(player.age),
      position: player.positionLabel || player.positionCode || "",
      goals: formatInteger(player.goals),
      assists: formatInteger(player.assists),
      xg: formatDecimal(player.xg),
      smrt_index: formatInteger(player.smrtIndex),
    }));
  }, []);

  const downloadExportedPdf = useCallback((response) => {
    const blob = new Blob([response.data], {
      type: response?.headers?.["content-type"] || "application/pdf",
    });
    const fileUrl = window.URL.createObjectURL(blob);
    const contentDisposition = response?.headers?.["content-disposition"] || "";
    const filenameMatch = contentDisposition.match(
      /filename\*?=(?:UTF-8''|")?([^";]+)/i
    );
    const filename = decodeURIComponent(
      (filenameMatch?.[1] || "smart-search-results.pdf")
        .replace(/"/g, "")
        .trim()
    );
    const link = document.createElement("a");

    link.href = fileUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(fileUrl);
  }, []);

  const exportPdfRows = useCallback(
    async (rows) => {
      const response = await $apiV2.post(
        SMART_SEARCH_EXPORT_ENDPOINT,
        {
          rows,
          locale: i18n?.language || "en",
          columns: SMART_SEARCH_EXPORT_COLUMNS,
        },
        {
          responseType: "blob",
        }
      );

      downloadExportedPdf(response);
    },
    [downloadExportedPdf, i18n]
  );

  const buildAllPagesExportParams = useCallback(
    (pageSizeOverride) =>
      filterObjectFromEmptyValues(
        buildSearchRequestParams({
          filters,
          page: 1,
          pageSize: pageSizeOverride,
          sort: sortKey,
          additionalParamModes: buildFeaturedParamModes(filters),
        })
      ),
    [buildFeaturedParamModes, filters, sortKey]
  );

  const runSynchronousAllPagesExport = useCallback(async () => {
    if (totalPlayers > MAX_ALL_PAGES_EXPORT_COUNT) {
      addNotification(t("smrtSearch.exportPdfLimitReached"), "warning");
      return false;
    }

    const response = await $apiV2.get(SMART_SEARCH_RESULTS_ENDPOINT, {
      params: buildAllPagesExportParams(totalPlayers),
    });
    const exportPlayers = sortSummaryResults(
      (response?.data?.results || []).map((player) =>
        summarizeSearchResult(player, {
          selectedParamIds: resultAdvancedParamIds,
          paramLabels: advancedParamLabelMap,
          advancedParamRanges: appliedAdvancedParamRanges,
        })
      ),
      sortKey
    );

    await exportPdfRows(buildExportRows(exportPlayers));
    return true;
  }, [
    addNotification,
    advancedParamLabelMap,
    appliedAdvancedParamRanges,
    buildAllPagesExportParams,
    buildExportRows,
    exportPdfRows,
    resultAdvancedParamIds,
    sortKey,
    t,
    totalPlayers,
  ]);

  const queueAllPagesPdfExport = useCallback(
    async (fcmToken) => {
      const searchParams = buildAllPagesExportParams(RESULT_PAGE_SIZE);
      const response = await $apiV2.post(SMART_SEARCH_EXPORT_ASYNC_ENDPOINT, {
        search_params: searchParams,
        sort: searchParams.sort,
        locale: i18n?.language || "en",
        columns: SMART_SEARCH_EXPORT_COLUMNS,
        scope: EXPORT_SCOPE_OPTIONS.ALL,
        fcm_token: fcmToken,
      });

      return response?.data;
    },
    [buildAllPagesExportParams, i18n]
  );

  const handleExportPdf = useCallback(
    async (scope) => {
      if (!canExportPdf || isExportingPdf) {
        setExportScope("");
        return;
      }

      setIsExportingPdf(true);

      try {
        if (scope === EXPORT_SCOPE_OPTIONS.ALL) {
          const pushTokenResult = await requestPushNotificationToken();
          const shouldUseAsyncExport =
            pushTokenResult?.status === "granted" && pushTokenResult?.token;

          if (shouldUseAsyncExport) {
            await queueAllPagesPdfExport(pushTokenResult.token);
            addNotification(t("smrtSearch.exportPdfQueued"), "info");
          } else {
            if (pushTokenResult?.status === "denied") {
              addNotification(t("smrtSearch.exportPdfPermissionWarning"), "warning");
            } else if (
              pushTokenResult?.status &&
              pushTokenResult.status !== "dismissed"
            ) {
              addNotification(t("smrtSearch.exportPdfPushUnavailable"), "warning");
            }

            await runSynchronousAllPagesExport();
          }
        } else {
          await exportPdfRows(buildExportRows(summarizedPlayers));
        }
      } catch (error) {
        addNotification(
          normalizeErrorMessage(error, t("smrtSearch.exportPdfError")),
          "error"
        );
      } finally {
        setIsExportingPdf(false);
        setExportScope("");
      }
    },
    [
      addNotification,
      buildExportRows,
      canExportPdf,
      exportPdfRows,
      isExportingPdf,
      queueAllPagesPdfExport,
      runSynchronousAllPagesExport,
      summarizedPlayers,
      t,
    ]
  );

  const handleExportScopeChange = useCallback(
    (event) => {
      const nextScope = event.target.value;

      setExportScope(nextScope);

      if (!nextScope) {
        return;
      }

      void handleExportPdf(nextScope);
    },
    [handleExportPdf]
  );

  const applyTemplateToView = useCallback(
    (templateState) => {
      const nextFilters = applyLegacyModeToActiveRows(
        applyState(templateState, positions, {
          advancedParameters: advancedParamCatalog.items,
          featuredAdvancedFieldMap: FEATURED_ADVANCED_FIELD_MAP,
        }),
        getTemplateStateStatMode(templateState)
      );
      const nextPage = 1;
      const hasSavedSort =
        templateState &&
        typeof templateState === "object" &&
        Object.prototype.hasOwnProperty.call(templateState, "sort");
      const nextSort = hasSavedSort
        ? normalizeSearchSortKey(templateState.sort)
        : sortKey;

      setFilters(nextFilters);
      setPage(nextPage);
      setSortKey(nextSort);
      setTemplateStatusMessage(null);

      // If the template has advanced parameter ranges, open the advanced section
      // and auto-expand the groups that contain the saved params so the user can see them.
      const advancedRanges = nextFilters.advancedParamRanges || {};
      if (Object.keys(advancedRanges).length > 0) {
        setAdvancedOpen(true);

        if (advancedParamCatalog.groups.length > 0) {
          const appliedParamIds = new Set(Object.keys(advancedRanges));
          const groupsToExpand = {};
          advancedParamCatalog.groups.forEach((group) => {
            if (group.items.some((item) => appliedParamIds.has(String(item.id)))) {
              groupsToExpand[group.key] = true;
            }
          });
          if (Object.keys(groupsToExpand).length > 0) {
            setManualAdvancedGroups((prev) => ({ ...prev, ...groupsToExpand }));
          }
        }
      }

      executeSearch({
        nextFilters,
        nextPage,
        nextSort,
        sync: true,
        revealResults: true,
      });
    },
    [advancedParamCatalog, applyLegacyModeToActiveRows, executeSearch, positions, sortKey]
  );

  const {
    accessDenied: templateAccessDenied,
    clearSelectedTemplateSelection,
    deleteModalOpen: remoteDeleteModalOpen,
    dropdownOpen: templateDropdownOpen,
    handleApplySelectedTemplate: handleApplyRemoteTemplate,
    handleCloseDeleteModal: handleCloseRemoteDeleteModal,
    handleCloseSaveModal: closeRemoteTemplateModal,
    handleDeleteTemplate: handleDeleteRemoteTemplate,
    handleDropdownToggle: handleTemplateDropdownToggle,
    handleOpenCreateModal: openRemoteCreateModal,
    handleOpenDeleteModal: openRemoteDeleteModal,
    handleOpenUpdateModal: openRemoteUpdateModal,
    handleSaveTemplate: saveRemoteTemplate,
    handleTemplateTitleChange: handleRemoteTemplateTitleChange,
    isDeletingTemplate: isDeletingRemoteTemplate,
    isLoadingTemplates: isLoadingRemoteTemplates,
    isSavingTemplate: isSavingRemoteTemplate,
    pendingDeleteTemplate: pendingRemoteDeleteTemplate,
    refreshTemplates: refreshRemoteTemplates,
    saveErrorMessage: remoteSaveErrorMessage,
    saveModalOpen: remoteSaveModalOpen,
    saveMode: remoteSaveMode,
    selectedTemplate: activeRemoteTemplate,
    setDropdownOpen: setTemplateDropdownOpen,
    statusMessage: remoteStatusMessage,
    templateTitle: remoteTemplateTitle,
    templateTitleMaxLength: remoteTemplateTitleMaxLength,
    templates: remoteTemplates,
    templatesError: remoteTemplatesError,
    titleError: remoteTitleError,
  } = useSavedFilterTemplates({
    pageKey: SMRT_SEARCH_TEMPLATE_PAGE_KEY,
    filters: captureState(filters, positions, {
      advancedParameters: advancedParamCatalog.items,
      featuredAdvancedFieldMap: FEATURED_ADVANCED_FIELD_MAP,
      sort: sortKey,
    }),
    onApplyTemplate: (templateFilters) => {
      applyTemplateToView(templateFilters);
    },
    onSaveSuccess: (savedTemplate) => {
      setTemplateStatusMessage(null);
      showToast(
        t("smrtSearch.templateSavedToast", {
          name: savedTemplate?.title || "",
        })
      );
    },
  });

  const mappedSavedTemplates = useMemo(
    () =>
      remoteTemplates.map((template) => ({
        id: `remote:${template.id}`,
        title: template.title,
        source: "remote",
        remoteId: Number(template.id),
        remoteTemplate: template,
      })),
    [remoteTemplates]
  );

  const selectedRemoteTemplate = useMemo(() => {
    if (!activeRemoteTemplate?.id) {
      return null;
    }

    return (
      mappedSavedTemplates.find(
        (template) => template.id === `remote:${activeRemoteTemplate.id}`
      ) || null
    );
  }, [activeRemoteTemplate, mappedSavedTemplates]);

  const selectedTemplate = selectedRemoteTemplate || null;

  const templatesError = mappedSavedTemplates.length ? "" : remoteTemplatesError;
  const isLoadingTemplates =
    isLoadingRemoteTemplates && mappedSavedTemplates.length === 0;
  const activeDeleteModalOpen = remoteDeleteModalOpen;
  const pendingDeleteTemplate = pendingRemoteDeleteTemplate;
  const statusMessage = templateStatusMessage || remoteStatusMessage;

  const showTemplateValidationMessage = useCallback(() => {
    setTemplateStatusMessage({
      type: "error",
      text: t("savedTemplates.filtersRequired"),
    });
  }, [t]);

  const ensureTemplateFiltersSelected = useCallback(() => {
    if (hasVisibleFilters(filters)) {
      setTemplateStatusMessage(null);
      return true;
    }

    setTemplateDropdownOpen(false);
    closeRemoteTemplateModal();
    showTemplateValidationMessage();
    return false;
  }, [
    closeRemoteTemplateModal,
    filters,
    setTemplateDropdownOpen,
    showTemplateValidationMessage,
  ]);

  const handleOpenTemplateModal = useCallback(() => {
    if (!ensureTemplateFiltersSelected()) {
      return;
    }

    setTemplateDropdownOpen(false);
    openRemoteCreateModal();
  }, [
    ensureTemplateFiltersSelected,
    openRemoteCreateModal,
    setTemplateDropdownOpen,
  ]);

  const handleOpenUpdateTemplateModal = useCallback(() => {
    if (!ensureTemplateFiltersSelected()) {
      return;
    }

    setTemplateDropdownOpen(false);
    openRemoteUpdateModal();
  }, [
    ensureTemplateFiltersSelected,
    openRemoteUpdateModal,
    setTemplateDropdownOpen,
  ]);

  const handleSaveTemplate = useCallback(() => {
    if (!ensureTemplateFiltersSelected()) {
      return;
    }

    saveRemoteTemplate();
  }, [ensureTemplateFiltersSelected, saveRemoteTemplate]);

  const handleApplySelectedTemplate = useCallback(
    (template) => {
      if (template?.source === "remote" && template?.remoteTemplate) {
        handleApplyRemoteTemplate(template.remoteTemplate);
        showToast(
          t("smrtSearch.templateLoadedToast", { name: template.title })
        );
      }
    },
    [handleApplyRemoteTemplate, showToast, t]
  );

  const handleCloseDeleteModal = useCallback(() => {
    handleCloseRemoteDeleteModal();
  }, [handleCloseRemoteDeleteModal]);

  const handleOpenDeleteModal = useCallback(
    (template) => {
      setTemplateStatusMessage(null);

      if (template?.source === "remote" && template?.remoteTemplate) {
        openRemoteDeleteModal(template.remoteTemplate);
      }
    },
    [openRemoteDeleteModal]
  );

  const handleDeleteTemplate = useCallback(() => {
    handleDeleteRemoteTemplate();
  }, [handleDeleteRemoteTemplate]);

  const handleOpenProfile = useCallback((player) => {
    trackCustomEvent({
      action: "open",
      entityId: player?.id,
      entityLabel: player?.fullName || player?.known_name || player?.display_name || null,
      entityType: "search_result",
      eventType: "smrt_search_result_opened",
      metadata: {
        source_surface: "smrt_search",
      },
      sourceSurface: "smrt_search",
    });
    openNewTab(`/tournaments/player/${player.id}`);
  }, []);

  const handleComparePlayer = useCallback(
    async (player) => {
      try {
        await $apiV2.post("/platform/compare/add", {
          player_id: player.id,
        });
        trackCompareAction({
          action: "add",
          metadata: {
            source_surface: "smrt_search",
          },
          playerId: player?.id,
          playerName: player?.fullName || player?.known_name || player?.display_name || null,
          sourceSurface: "smrt_search",
        });

        // Update compare_workspace_draft_v1 in localStorage (the format the Compare page reads)
        const COMPARE_WORKSPACE_DRAFT_STORAGE_KEY = "compare_workspace_draft_v1";
        const playerId = Number(player.id);
        let draft = { selectedCompareParams: [], players: [] };

        try {
          const raw = localStorage.getItem(COMPARE_WORKSPACE_DRAFT_STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
              draft = {
                selectedCompareParams: Array.isArray(parsed.selectedCompareParams)
                  ? parsed.selectedCompareParams
                  : [],
                players: Array.isArray(parsed.players) ? parsed.players : [],
              };
            }
          }
        } catch (e) {
          // ignore parse errors, start with empty draft
        }

        // Prevent duplicate players
        const alreadyExists = draft.players.some(
          (p) => Number(p.playerId ?? p.player_id ?? p.id) === playerId
        );

        if (!alreadyExists) {
          draft.players.push({ playerId, yearSeasonId: null });
          localStorage.setItem(
            COMPARE_WORKSPACE_DRAFT_STORAGE_KEY,
            JSON.stringify(draft)
          );
        }

        addNotification(
          t("smrtSearch.compareAddedMessage", { name: player.fullName }),
          "success"
        );
      } catch (error) {
        addNotification(
          normalizeErrorMessage(error, t("smrtSearch.compareErrorMessage")),
          "error"
        );
      }
    },
    [addNotification, t]
  );

  const handleCloseShadowTeamModal = useCallback(() => {
    setShadowTeamModalPlayer(null);
    setShadowTeamSelectedTeamId(null);
    setShadowTeamLoadingTeamId(null);
  }, []);

  const handleOpenShadowTeam = useCallback(
    async (player) => {
      setShadowTeamModalPlayer(player);
      setShadowTeamSelectedTeamId(null);
      setShadowTeamLoadingTeamId(null);

      if (shadowTeams.length > 0) {
        return;
      }

      try {
        await dispatch(fetchShadowTeams()).unwrap();
      } catch (error) {
        addNotification(
          normalizeErrorMessage(error, t("shadowTeam.loadError")),
          "error"
        );
      }
    },
    [addNotification, dispatch, shadowTeams.length, t]
  );

  const handleSelectShadowTeam = useCallback(
    async (team) => {
      const teamId = Number(team?.id);

      if (!Number.isFinite(teamId)) {
        return;
      }

      setShadowTeamSelectedTeamId(teamId);
      setShadowTeamLoadingTeamId(teamId);

      try {
        await dispatch(fetchShadowTeamById(teamId)).unwrap();
      } catch (error) {
        setShadowTeamSelectedTeamId(null);
        addNotification(
          normalizeErrorMessage(error, t("shadowTeam.loadError")),
          "error"
        );
      } finally {
        setShadowTeamLoadingTeamId((currentValue) =>
          currentValue === teamId ? null : currentValue
        );
      }
    },
    [addNotification, dispatch, t]
  );

  const handleAddToShadowTeamSlot = useCallback(
    async (slot) => {
      if (
        !shadowTeamModalPlayer ||
        !selectedShadowTeam ||
        isShadowTeamSlotFull(slot)
      ) {
        return;
      }

      try {
        await dispatch(
          addShadowTeamPlayer({
            shadowTeamId: selectedShadowTeam.id,
            playerId: Number(shadowTeamModalPlayer.id),
            positionGroup: slot.positionGroup,
            positionOrder: slot.positionOrder,
          })
        ).unwrap();
        trackShadowTeamAction({
          action: "player_add",
          metadata: {
            position_group: slot.positionGroup,
            position_order: slot.positionOrder,
            slot_key: slot.key,
          },
          playerId: Number(shadowTeamModalPlayer?.id),
          playerName: shadowTeamModalPlayerName,
          shadowTeamId: Number(selectedShadowTeam?.id),
          shadowTeamName: selectedShadowTeam?.name || null,
          sourceSurface: "smrt_search",
        });

        addNotification(
          t("smrtSearch.shadowTeamAddedMessage", {
            name: shadowTeamModalPlayerName,
            team: selectedShadowTeam.name,
            slot: slot.shortLabel,
          }),
          "success"
        );
        handleCloseShadowTeamModal();
      } catch (error) {
        addNotification(
          normalizeErrorMessage(error, t("smrtSearch.shadowTeamErrorMessage")),
          "error"
        );
      }
    },
    [
      addNotification,
      dispatch,
      handleCloseShadowTeamModal,
      isShadowTeamSlotFull,
      selectedShadowTeam,
      shadowTeamModalPlayer,
      shadowTeamModalPlayerName,
      t,
    ]
  );

  const handleToggleFavorite = useCallback(
    async (player) => {
      const isFavorite = Boolean(favoritePlayers[player.id]);

      try {
        if (isFavorite) {
          await $apiV1.delete(`/users/favorites/players/${player.id}/`);
        } else {
          await $apiV1.post("/users/favorites/players/", {
            player: player.id,
          });
        }
        trackFavoriteChange({
          favoriteStateAfter: !isFavorite,
          favoriteStateBefore: isFavorite,
          metadata: {
            source_surface: "smrt_search",
          },
          playerId: player?.id,
          playerName: player?.known_name || player?.fullName || player?.display_name || null,
          sourceSurface: "smrt_search",
        });

        setFavoritePlayers((previousFavorites) => ({
          ...previousFavorites,
          [player.id]: !isFavorite,
        }));

        addNotification(
          t(
            isFavorite
              ? "smrtSearch.favoriteRemovedMessage"
              : "smrtSearch.favoriteAddedMessage",
            { name: player.known_name || player.fullName }
          ),
          "success"
        );
      } catch (error) {
        addNotification(
          normalizeErrorMessage(error, t("smrtSearch.favoriteErrorMessage")),
          "error"
        );
      }
    },
    [addNotification, favoritePlayers, t]
  );

  const handleChipRemove = (chipKey) => {
    setFilters((previousFilters) =>
      clearChip(previousFilters, chipKey, positions)
    );
  };

  const handleAdvancedParamRangeChange = useCallback(
    (paramId, updates) => {
      setFilters((previousFilters) => {
        const nextAdvancedParamRanges = {
          ...(previousFilters.advancedParamRanges || {}),
        };
        const previousRange = nextAdvancedParamRanges[paramId] || {
          from: "",
          to: "",
          mode: STAT_MODES.SEASON,
        };
        const nextRange = {
          ...previousRange,
          ...updates,
        };
        const normalizedRange = {
          from: String(nextRange.from || ""),
          to: String(nextRange.to || ""),
          mode: normalizeSearchStatMode(nextRange.mode),
        };

        if (
          !normalizedRange.from &&
          !normalizedRange.to &&
          normalizedRange.mode === STAT_MODES.SEASON
        ) {
          delete nextAdvancedParamRanges[paramId];
        } else {
          nextAdvancedParamRanges[paramId] = normalizedRange;
        }

        return {
          ...previousFilters,
          advancedParamRanges: nextAdvancedParamRanges,
        };
      });
    },
    []
  );

  const activeAdvancedParamModes = useMemo(
    () =>
      advancedParamCatalog.items.reduce((modes, parameter) => {
        const paramId = String(parameter.id);
        const staticFieldKeys = FEATURED_ADVANCED_FIELD_MAP[parameter.featuredKey];
        const hasActiveRange = staticFieldKeys
          ? Boolean(filters[staticFieldKeys[0]] || filters[staticFieldKeys[1]])
          : Boolean(
              filters.advancedParamRanges?.[paramId]?.from ||
                filters.advancedParamRanges?.[paramId]?.to
            );

        if (hasActiveRange) {
          modes.push(
            normalizeSearchStatMode(filters.advancedParamRanges?.[paramId]?.mode)
          );
        }

        return modes;
      }, []),
    [advancedParamCatalog.items, filters]
  );

  const globalAdvancedParamMode = useMemo(() => {
    if (!activeAdvancedParamModes.length) {
      return globalAdvancedModeChoice;
    }

    return activeAdvancedParamModes.every(
      (mode) => mode === activeAdvancedParamModes[0]
    )
      ? activeAdvancedParamModes[0]
      : "";
  }, [activeAdvancedParamModes, globalAdvancedModeChoice]);

  const handleApplyGlobalAdvancedParamMode = useCallback(
    (nextModeValue) => {
      const mode = normalizeSearchStatMode(nextModeValue);

      setGlobalAdvancedModeChoice(mode);

      setFilters((previousFilters) => {
        const nextAdvancedParamRanges = {
          ...(previousFilters.advancedParamRanges || {}),
        };

        advancedParamCatalog.items.forEach((parameter) => {
          const paramId = String(parameter.id);

          nextAdvancedParamRanges[paramId] = {
            ...(nextAdvancedParamRanges[paramId] || {}),
            mode,
          };
        });

        return {
          ...previousFilters,
          advancedParamRanges: nextAdvancedParamRanges,
        };
      });
    },
    [advancedParamCatalog.items]
  );

  const handleAdvancedGroupToggle = useCallback((groupKey) => {
    setManualAdvancedGroups((previousGroups) => ({
      ...previousGroups,
      [groupKey]: !previousGroups[groupKey],
    }));
  }, []);

  const handleSearchSubmit = () => {
    if (!hasSearchScopeFilters(filters)) {
      addNotification(
        t("smrtSearch.scopeFilterRequired"),
        "warning"
      );
      return;
    }

    const nextPage = 1;
    setPage(nextPage);
    executeSearch({
      nextFilters: filters,
      nextPage,
      nextSort: sortKey,
      sync: true,
      revealResults: true,
    });
  };

  const handleSortChange = (event) => {
    const nextSortKey = event.target.value;
    const nextPage = 1;

    setSortKey(nextSortKey);
    setPage(nextPage);

    if (!hasSearched) {
      return;
    }

    executeSearch({
      nextFilters: filters,
      nextPage,
      nextSort: nextSortKey,
      sync: true,
      revealResults: true,
    });
  };

  const handlePageChange = (nextPage) => {
    if (
      loadingSearch ||
      nextPage < 1 ||
      nextPage > totalPages ||
      nextPage === page
    ) {
      return;
    }

    setPage(nextPage);
    executeSearch({
      nextFilters: filters,
      nextPage,
      nextSort: sortKey,
      sync: true,
      revealResults: true,
    });
  };

  const renderChipLabel = (chip) => {
    if (!chip.key.startsWith("qf:")) {
      return chip.label;
    }

    return quickFilterLabelMap[chip.key.slice(3)] || chip.label;
  };

  const getVisiblePlayerStats = (player, { compact = false } = {}) => {
    const baseStats = [
      {
        key: "goals",
        label: t("smrtSearch.sortGoals"),
        value: formatInteger(player.goals),
      },
      {
        key: "assists",
        label: compact
          ? t("smrtSearch.sortAssistsShort")
          : t("smrtSearch.sortAssists"),
        value: formatInteger(player.assists),
      },
      {
        key: "xg",
        label: t("smrtSearch.sortXg"),
        value: formatDecimal(player.xg),
      },
    ];

    const selectedStats = Array.isArray(player.selectedStats)
      ? player.selectedStats.map((stat) => ({
          key: `param-${stat.id}`,
          label: stat.label,
          value: formatFlexibleStatValue(stat.value),
        }))
      : [];

    const visibleStats = [...baseStats, ...selectedStats];

    return compact ? visibleStats : visibleStats.slice(0, 4);
  };

  const renderAdvancedParamRow = (parameter) => {
    const paramId = String(parameter.id);
    const staticFieldKeys = FEATURED_ADVANCED_FIELD_MAP[parameter.featuredKey];
    const activeMode = normalizeSearchStatMode(
      filters.advancedParamRanges?.[paramId]?.mode
    );
    const activeRange = staticFieldKeys
      ? {
          from: filters[staticFieldKeys[0]] || "",
          to: filters[staticFieldKeys[1]] || "",
        }
      : filters.advancedParamRanges?.[paramId] || {
          from: "",
          to: "",
        };

    const handleRangeChange = (boundKey, nextValue) => {
      if (staticFieldKeys) {
        const fieldKey = boundKey === "from" ? staticFieldKeys[0] : staticFieldKeys[1];
        const [fromField, toField] = staticFieldKeys;

        setFilters((previousFilters) => {
          const nextFilters = {
            ...previousFilters,
            [fieldKey]: nextValue,
          };
          const nextAdvancedParamRanges = {
            ...(previousFilters.advancedParamRanges || {}),
          };
          const previousRange = nextAdvancedParamRanges[paramId] || {};
          const nextFromValue =
            boundKey === "from" ? nextValue : previousFilters[fromField] || "";
          const nextToValue =
            boundKey === "to" ? nextValue : previousFilters[toField] || "";
          const normalizedMode = normalizeSearchStatMode(previousRange.mode);

          if (
            !nextFromValue &&
            !nextToValue &&
            normalizedMode === STAT_MODES.SEASON
          ) {
            delete nextAdvancedParamRanges[paramId];
          } else {
            nextAdvancedParamRanges[paramId] = {
              ...previousRange,
              mode: normalizedMode,
            };
          }

          return {
            ...nextFilters,
            advancedParamRanges: nextAdvancedParamRanges,
          };
        });
        return;
      }

      handleAdvancedParamRangeChange(paramId, {
        [boundKey]: nextValue,
      });
    };

    const handleModeChange = (nextModeValue) => {
      if (staticFieldKeys) {
        const [fromField, toField] = staticFieldKeys;

        setFilters((previousFilters) => {
          const nextAdvancedParamRanges = {
            ...(previousFilters.advancedParamRanges || {}),
          };
          const previousRange = nextAdvancedParamRanges[paramId] || {};
          const hasRangeValue = Boolean(
            previousFilters[fromField] || previousFilters[toField]
          );
          const normalizedMode = normalizeSearchStatMode(nextModeValue);

          if (!hasRangeValue && normalizedMode === STAT_MODES.SEASON) {
            delete nextAdvancedParamRanges[paramId];
          } else {
            nextAdvancedParamRanges[paramId] = {
              ...previousRange,
              mode: normalizedMode,
            };
          }

          return {
            ...previousFilters,
            advancedParamRanges: nextAdvancedParamRanges,
          };
        });
        return;
      }

      handleAdvancedParamRangeChange(paramId, {
        mode: nextModeValue,
      });
    };

    return (
      <AdvancedParamRow key={paramId}>
        <AdvancedParamLabel>{parameter.label}</AdvancedParamLabel>
        <AdvancedParamInputs>
          <FieldInput
            aria-label={`${parameter.label} ${t("smrtSearch.fromLabel")}`}
            inputMode="decimal"
            placeholder={t("smrtSearch.fromLabel")}
            step="any"
            type="number"
            value={activeRange.from}
            onChange={(event) =>
              handleRangeChange("from", event.target.value)
            }
          />
          <FieldInput
            aria-label={`${parameter.label} ${t("smrtSearch.toLabel")}`}
            inputMode="decimal"
            placeholder={t("smrtSearch.toLabel")}
            step="any"
            type="number"
            value={activeRange.to}
            onChange={(event) =>
              handleRangeChange("to", event.target.value)
            }
          />
          <FieldControl
            aria-label={`${parameter.label} ${t("smrtSearch.dataModeLabel")}`}
            value={activeMode}
            onChange={(event) => handleModeChange(event.target.value)}
          >
            {statModeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FieldControl>
        </AdvancedParamInputs>
      </AdvancedParamRow>
    );
  };

  const tutorialCurrentStep = showTutorial
    ? smrtSearchTutorialSteps[tutorialStep]
    : null;
  const tutorialIsLastStep =
    tutorialStep >= smrtSearchTutorialSteps.length - 1;
  const tutorialHelpBottom =
    typeof window !== "undefined" && window.innerWidth <= 768 ? 12 : 16;
  const tutorialSpotlightPadding = 10;
  const tutorialSpotlightStyle = tutorialRect
    ? {
        left: tutorialRect.left - tutorialSpotlightPadding,
        top: tutorialRect.top - tutorialSpotlightPadding,
        width: tutorialRect.width + tutorialSpotlightPadding * 2,
        height: tutorialRect.height + tutorialSpotlightPadding * 2,
      }
    : null;

  const tutorialCardStyle = (() => {
    const cardWidth = 340;
    const viewportWidth =
      typeof window !== "undefined" ? window.innerWidth : cardWidth + 40;
    const viewportHeight =
      typeof window !== "undefined" ? window.innerHeight : 700;

    if (!tutorialCurrentStep) {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: cardWidth,
      };
    }

    if (tutorialCurrentStep.id === "welcome") {
      if (viewportWidth <= 420) {
        return {
          position: "fixed",
          left: 16,
          right: 16,
          bottom: 16,
          top: "auto",
          transform: "none",
          width: "auto",
        };
      }

      return {
        position: "fixed",
        right: 24,
        bottom: 24,
        width: cardWidth,
        left: "auto",
        top: "auto",
        transform: "none",
      };
    }

    if (!tutorialRect || tutorialCurrentStep.position === "center") {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: cardWidth,
      };
    }

    let top = 16;
    let left = tutorialRect.left + tutorialRect.width / 2 - cardWidth / 2;

    if (tutorialCurrentStep.position === "below") {
      top = tutorialRect.bottom + tutorialSpotlightPadding + 18;
    } else if (tutorialCurrentStep.position === "above") {
      top = tutorialRect.top - tutorialSpotlightPadding - 20 - 180;
    }

    left = Math.max(16, Math.min(left, viewportWidth - cardWidth - 16));
    top = Math.max(16, Math.min(top, viewportHeight - 220));

    return {
      position: "fixed",
      top,
      left,
      width: cardWidth,
    };
  })();

  return (
    <MainWrapper>
      <Navigation />
      <MainWrapperFavorite
        className={navigationOpen === "true" ? "" : "fullWidthPage"}
      >
        <SmrtSearchWorkspace>
          <SmrtSearchTopBar>
            <TemplateHeaderTitle>{t("smrtSearch.title")}</TemplateHeaderTitle>

            <TemplateControlsRow ref={templateControlsRef}>
              <div style={{ minWidth: 250, flex: "0 1 250px" }}>
                <SavedFilterTemplatesControlsView
                  accessDenied={templateAccessDenied}
                  deleteModalOpen={activeDeleteModalOpen}
                  dropdownOpen={templateDropdownOpen}
                  isDeletingTemplate={isDeletingRemoteTemplate}
                  isLoadingTemplates={isLoadingTemplates}
                  isSavingTemplate={isSavingRemoteTemplate}
                  pageKey={SMRT_SEARCH_TEMPLATE_PAGE_KEY}
                  pendingDeleteTemplate={pendingDeleteTemplate}
                  refreshTemplates={refreshRemoteTemplates}
                  saveErrorMessage={remoteSaveErrorMessage}
                  saveModalOpen={remoteSaveModalOpen}
                  saveMode={remoteSaveMode}
                  selectedTemplate={selectedTemplate}
                  setDropdownOpen={setTemplateDropdownOpen}
                  statusMessage={statusMessage}
                  templateTitle={remoteTemplateTitle}
                  templateTitleMaxLength={remoteTemplateTitleMaxLength}
                  templates={mappedSavedTemplates}
                  templatesError={templatesError}
                  titleError={remoteTitleError}
                  handleApplySelectedTemplate={handleApplySelectedTemplate}
                  handleCloseDeleteModal={handleCloseDeleteModal}
                  handleCloseSaveModal={closeRemoteTemplateModal}
                  handleDeleteTemplate={handleDeleteTemplate}
                  handleDropdownToggle={handleTemplateDropdownToggle}
                  handleOpenCreateModal={handleOpenTemplateModal}
                  handleOpenDeleteModal={handleOpenDeleteModal}
                  handleOpenUpdateModal={handleOpenUpdateTemplateModal}
                  handleSaveTemplate={handleSaveTemplate}
                  handleTemplateTitleChange={handleRemoteTemplateTitleChange}
                  showCreateButton={false}
                  showUpdateButton={false}
                  showStatusMessage
                />
              </div>

              {selectedRemoteTemplate ? (
                <PanelToggleButton
                  type="button"
                  onClick={handleOpenUpdateTemplateModal}
                >
                  {t("savedTemplates.update")}
                </PanelToggleButton>
              ) : null}

              <PanelToggleButton type="button" onClick={handleOpenTemplateModal}>
                {t("savedTemplates.save")}
              </PanelToggleButton>

              <ResultsSortSelect
                aria-label={t("smrtSearch.exportPdf")}
                disabled={!canExportPdf || isExportingPdf}
                value={exportScope}
                onChange={handleExportScopeChange}
              >
                <option value="">
                  {isExportingPdf
                    ? t("smrtSearch.exportingPdf")
                    : t("smrtSearch.exportPdf")}
                </option>
                <option value={EXPORT_SCOPE_OPTIONS.CURRENT}>
                  {t("smrtSearch.exportPdfCurrentPage")}
                </option>
                <option value={EXPORT_SCOPE_OPTIONS.ALL}>
                  {t("smrtSearch.exportPdfAllPages")}
                </option>
              </ResultsSortSelect>
            </TemplateControlsRow>
          </SmrtSearchTopBar>

          <SmrtSearchPanel>
            <FiltersGrid ref={filtersGridRef}>
              <DropdownField
                id="smrt-search-competition"
                label={t("compareWorkspace.filterCompetition")}
                isOpen={competitionOpen}
                onClose={() => setCompetitionOpen(false)}
                onToggle={() => setCompetitionOpen((previousOpen) => !previousOpen)}
                summary={selectedCompetitionLabels.join(", ")}
                placeholder={t("smrtSearch.anyOption")}
              >
                <FilterDropdownSearchInput
                  aria-label={t("compareWorkspace.filterCompetition")}
                  placeholder={t("compareWorkspace.filterCompetition")}
                  value={competitionSearchTerm}
                  onChange={(event) => {
                    setCompetitionSearchTerm(event.target.value);
                  }}
                />
                <FilterDropdownOptions ref={competitionListRef}>
                  {orderedCompetitionOptions.length ? (
                    orderedCompetitionOptions.map((competition) => {
                      const competitionId = getOptionId(competition);
                      const competitionLabel = getOptionDisplayLabel(competition);
                      const isSelected = filters.selectedCompetition.includes(
                        competitionId
                      );

                      return (
                        <FilterDropdownOptionButton
                          key={competitionId}
                          type="button"
                          $active={isSelected}
                          onClick={() =>
                            toggleMultiFilterValue(
                              "selectedCompetition",
                              competitionId
                            )
                          }
                        >
                          <FilterDropdownOptionText title={competitionLabel}>
                            {competitionLabel}
                          </FilterDropdownOptionText>
                          <FilterDropdownOptionCheck $active={isSelected}>
                            ✓
                          </FilterDropdownOptionCheck>
                        </FilterDropdownOptionButton>
                      );
                    })
                  ) : (
                    <FilterDropdownEmptyState>
                      {competitionLoading
                        ? t("compareWorkspace.loading")
                        : t("smrtSearch.emptySearchTitle")}
                    </FilterDropdownEmptyState>
                  )}
                  {competitionHasMore ? (
                    <div
                      ref={competitionLoadMoreRef}
                      style={{ width: "100%", height: 1 }}
                    />
                  ) : null}
                </FilterDropdownOptions>
                {competitionLoading && orderedCompetitionOptions.length ? (
                  <FilterDropdownEmptyState>
                    {t("compareWorkspace.loading")}
                  </FilterDropdownEmptyState>
                ) : null}
              </DropdownField>

              <DropdownField
                id="smrt-search-season"
                label={t("compareWorkspace.filterSeason")}
                isOpen={seasonOpen}
                onClose={() => setSeasonOpen(false)}
                onToggle={() => setSeasonOpen((previousOpen) => !previousOpen)}
                summary={selectedSeasonLabels.join(", ")}
                placeholder={t("smrtSearch.anyOption")}
              >
                <FilterDropdownSearchInput
                  aria-label={t("compareWorkspace.filterSeason")}
                  placeholder={t("compareWorkspace.filterSeason")}
                  value={seasonSearchTerm}
                  onChange={(event) => {
                    setSeasonSearchTerm(event.target.value);
                  }}
                />
                <FilterDropdownOptions>
                  {displayedSeasonOptions.length ? (
                    displayedSeasonOptions.map((season) => {
                      const seasonId = getOptionId(season);
                      const seasonLabel = getSeasonOptionLabel(season);
                      const isSelected = filters.selectedSeason.includes(seasonId);

                      return (
                        <FilterDropdownOptionButton
                          key={seasonId}
                          type="button"
                          $active={isSelected}
                          onClick={() =>
                            toggleMultiFilterValue("selectedSeason", seasonId)
                          }
                        >
                          <FilterDropdownOptionText title={seasonLabel}>
                            {seasonLabel}
                          </FilterDropdownOptionText>
                          <FilterDropdownOptionCheck $active={isSelected}>
                            ✓
                          </FilterDropdownOptionCheck>
                        </FilterDropdownOptionButton>
                      );
                    })
                  ) : (
                    <FilterDropdownEmptyState>
                      {seasonLoading
                        ? t("compareWorkspace.loading")
                        : t("smrtSearch.emptySearchTitle")}
                    </FilterDropdownEmptyState>
                  )}
                  {canRevealMoreSeasonOptions || seasonHasMore ? (
                    <FilterDropdownActionButton
                      type="button"
                      disabled={seasonLoading}
                      onClick={handleSeasonLoadMore}
                    >
                      {seasonLoading
                        ? t("compareWorkspace.loading")
                        : t("smrtSearch.loadMore")}
                    </FilterDropdownActionButton>
                  ) : null}
                </FilterDropdownOptions>
              </DropdownField>

              <DropdownField
                id="smrt-search-nationality"
                label={t("smrtSearch.nationalityLabel")}
                isOpen={nationalityOpen}
                onClose={() => setNationalityOpen(false)}
                onToggle={() => setNationalityOpen((previousOpen) => !previousOpen)}
                summary={selectedNationLabels.join(", ")}
                placeholder={t("smrtSearch.anyOption")}
              >
                <FilterDropdownSearchInput
                  aria-label={t("smrtSearch.nationalityLabel")}
                  placeholder={t("smrtSearch.nationalityLabel")}
                  value={nationalitySearchTerm}
                  onChange={(event) => {
                    setNationalitySearchTerm(event.target.value);
                  }}
                />
                <FilterDropdownOptions>
                  <FilterDropdownOptionButton
                    type="button"
                    $active={!filters.selectedNation.length}
                    onClick={() => setSelectedNationValue("")}
                  >
                    <FilterDropdownOptionText title={t("smrtSearch.anyOption")}>
                      {t("smrtSearch.anyOption")}
                    </FilterDropdownOptionText>
                    <FilterDropdownOptionCheck
                      $active={!filters.selectedNation.length}
                    >
                      ✓
                    </FilterDropdownOptionCheck>
                  </FilterDropdownOptionButton>
                  {orderedNationOptions.length ? (
                    orderedNationOptions.map((nation) => {
                      const nationId = getOptionId(nation);
                      const nationLabel = getOptionDisplayLabel(nation);
                      const isSelected = filters.selectedNation.includes(nationId);

                      return (
                        <FilterDropdownOptionButton
                          key={nationId}
                          type="button"
                          $active={isSelected}
                          onClick={() => setSelectedNationValue(nationId)}
                        >
                          <FilterDropdownOptionText title={nationLabel}>
                            {nationLabel}
                          </FilterDropdownOptionText>
                          <FilterDropdownOptionCheck $active={isSelected}>
                            ✓
                          </FilterDropdownOptionCheck>
                        </FilterDropdownOptionButton>
                      );
                    })
                  ) : (
                    <FilterDropdownEmptyState>
                      {t("smrtSearch.emptySearchTitle")}
                    </FilterDropdownEmptyState>
                  )}
                </FilterDropdownOptions>
              </DropdownField>

              <DropdownField
                id="smrt-search-position"
                label={t("smrtSearch.position")}
                isOpen={positionOpen}
                onClose={() => setPositionOpen(false)}
                onToggle={() => setPositionOpen((previousOpen) => !previousOpen)}
                summary={selectedPositionLabels.join(", ")}
                placeholder={t("smrtSearch.anyOption")}
              >
                <FilterDropdownSearchInput
                  aria-label={t("smrtSearch.position")}
                  placeholder={t("smrtSearch.position")}
                  value={positionSearchTerm}
                  onChange={(event) => {
                    setPositionSearchTerm(event.target.value);
                  }}
                />
                <FilterDropdownOptions>
                  {orderedPositionOptions.length ? (
                    orderedPositionOptions.map((position) => {
                      const positionId = getOptionId(position);
                      const positionLabel = getPositionOptionLabel(position);
                      const isSelected = filters.selectedPosition.includes(
                        positionId
                      );

                      return (
                        <FilterDropdownOptionButton
                          key={positionId}
                          type="button"
                          $active={isSelected}
                          onClick={() =>
                            toggleMultiFilterValue("selectedPosition", positionId)
                          }
                        >
                          <FilterDropdownOptionText title={positionLabel}>
                            {positionLabel}
                          </FilterDropdownOptionText>
                          <FilterDropdownOptionCheck $active={isSelected}>
                            ✓
                          </FilterDropdownOptionCheck>
                        </FilterDropdownOptionButton>
                      );
                    })
                  ) : (
                    <FilterDropdownEmptyState>
                      {t("smrtSearch.emptySearchTitle")}
                    </FilterDropdownEmptyState>
                  )}
                </FilterDropdownOptions>
              </DropdownField>

              <DropdownField
                id="smrt-search-leg"
                label={t("smrtSearch.legLabel")}
                isOpen={legOpen}
                onClose={() => setLegOpen(false)}
                onToggle={() => setLegOpen((previousOpen) => !previousOpen)}
                summary={selectedLegLabel}
                placeholder={t("smrtSearch.anyOption")}
              >
                <FilterDropdownOptions>
                  <FilterDropdownOptionButton
                    type="button"
                    $active={!filters.selectedLeg}
                    onClick={() => {
                      setSingleFilterValue("selectedLeg", "");
                      setLegOpen(false);
                    }}
                  >
                    <FilterDropdownOptionText title={t("smrtSearch.anyOption")}>
                      {t("smrtSearch.anyOption")}
                    </FilterDropdownOptionText>
                    <FilterDropdownOptionCheck $active={!filters.selectedLeg}>
                      ✓
                    </FilterDropdownOptionCheck>
                  </FilterDropdownOptionButton>
                  {LEG_OPTIONS.map((option) => {
                    const optionLabel = legLabelMap[option.id];
                    const isSelected = filters.selectedLeg === option.id;

                    return (
                      <FilterDropdownOptionButton
                        key={option.id}
                        type="button"
                        $active={isSelected}
                        onClick={() => {
                          setSingleFilterValue("selectedLeg", option.id);
                          setLegOpen(false);
                        }}
                      >
                        <FilterDropdownOptionText title={optionLabel}>
                          {optionLabel}
                        </FilterDropdownOptionText>
                        <FilterDropdownOptionCheck $active={isSelected}>
                          ✓
                        </FilterDropdownOptionCheck>
                      </FilterDropdownOptionButton>
                    );
                  })}
                </FilterDropdownOptions>
              </DropdownField>

              <DropdownField
                id="smrt-search-gender"
                label={t("smrtSearch.genderLabel")}
                isOpen={genderOpen}
                onClose={() => setGenderOpen(false)}
                onToggle={() => setGenderOpen((previousOpen) => !previousOpen)}
                summary={selectedGenderLabel}
                placeholder={t("smrtSearch.anyOption")}
              >
                <FilterDropdownOptions>
                  <FilterDropdownOptionButton
                    type="button"
                    $active={!filters.selectedGender}
                    onClick={() => {
                      setSingleFilterValue("selectedGender", "");
                      setGenderOpen(false);
                    }}
                  >
                    <FilterDropdownOptionText title={t("smrtSearch.anyOption")}>
                      {t("smrtSearch.anyOption")}
                    </FilterDropdownOptionText>
                    <FilterDropdownOptionCheck $active={!filters.selectedGender}>
                      ✓
                    </FilterDropdownOptionCheck>
                  </FilterDropdownOptionButton>
                  {GENDER_OPTIONS.map((option) => {
                    const optionLabel = genderLabelMap[option.id];
                    const isSelected = filters.selectedGender === option.id;

                    return (
                      <FilterDropdownOptionButton
                        key={option.id}
                        type="button"
                        $active={isSelected}
                        onClick={() => {
                          setSingleFilterValue("selectedGender", option.id);
                          setGenderOpen(false);
                        }}
                      >
                        <FilterDropdownOptionText title={optionLabel}>
                          {optionLabel}
                        </FilterDropdownOptionText>
                        <FilterDropdownOptionCheck $active={isSelected}>
                          ✓
                        </FilterDropdownOptionCheck>
                      </FilterDropdownOptionButton>
                    );
                  })}
                </FilterDropdownOptions>
              </DropdownField>

              <FilterField>
                <FilterLabel>{t("compareWorkspace.filterAge")}</FilterLabel>
                <RangeFieldRow>
                  <FieldInput
                    id="smrt-search-age-min"
                    type="number"
                    inputMode="numeric"
                    value={filters.olderAge}
                    placeholder={t("smrtSearch.minLabel")}
                    onChange={(event) =>
                      setFilters((previousFilters) => ({
                        ...previousFilters,
                        olderAge: event.target.value,
                      }))
                    }
                  />
                  <FieldInput
                    id="smrt-search-age-max"
                    type="number"
                    inputMode="numeric"
                    value={filters.underAge}
                    placeholder={t("smrtSearch.maxLabel")}
                    onChange={(event) =>
                      setFilters((previousFilters) => ({
                        ...previousFilters,
                        underAge: event.target.value,
                      }))
                    }
                  />
                </RangeFieldRow>
              </FilterField>

              <FilterField>
                <FilterLabel>{t("compareWorkspace.filterHeight")} (cm)</FilterLabel>
                <RangeFieldRow>
                  <FieldInput
                    id="smrt-search-height-min"
                    type="number"
                    inputMode="numeric"
                    value={filters.heightFrom}
                    placeholder={t("smrtSearch.minLabel")}
                    onChange={(event) =>
                      setFilters((previousFilters) => ({
                        ...previousFilters,
                        heightFrom: event.target.value,
                      }))
                    }
                  />
                  <FieldInput
                    id="smrt-search-height-max"
                    type="number"
                    inputMode="numeric"
                    value={filters.heightTo}
                    placeholder={t("smrtSearch.maxLabel")}
                    onChange={(event) =>
                      setFilters((previousFilters) => ({
                        ...previousFilters,
                        heightTo: event.target.value,
                      }))
                    }
                  />
                </RangeFieldRow>
              </FilterField>

              <DropdownField
                id="smrt-search-team-type"
                label={t("compareWorkspace.filterTeamType")}
                isOpen={teamTypeOpen}
                onClose={() => setTeamTypeOpen(false)}
                onToggle={() => setTeamTypeOpen((previousOpen) => !previousOpen)}
                summary={selectedTeamTypeLabel}
                placeholder={t("smrtSearch.anyOption")}
              >
                <FilterDropdownOptions>
                  <FilterDropdownOptionButton
                    type="button"
                    $active={!filters.selectedTeamType}
                    onClick={() => setSingleFilterValue("selectedTeamType", "")}
                  >
                    <FilterDropdownOptionText title={t("smrtSearch.anyOption")}>
                      {t("smrtSearch.anyOption")}
                    </FilterDropdownOptionText>
                    <FilterDropdownOptionCheck $active={!filters.selectedTeamType}>
                      ✓
                    </FilterDropdownOptionCheck>
                  </FilterDropdownOptionButton>
                  {TEAM_TYPE_OPTIONS.map((option) => {
                    const optionLabel = t(option.labelKey);
                    const isSelected = filters.selectedTeamType === option.id;

                    return (
                      <FilterDropdownOptionButton
                        key={option.id}
                        type="button"
                        $active={isSelected}
                        onClick={() =>
                          setSingleFilterValue("selectedTeamType", option.id)
                        }
                      >
                        <FilterDropdownOptionText title={optionLabel}>
                          {optionLabel}
                        </FilterDropdownOptionText>
                        <FilterDropdownOptionCheck $active={isSelected}>
                          ✓
                        </FilterDropdownOptionCheck>
                      </FilterDropdownOptionButton>
                    );
                  })}
                </FilterDropdownOptions>
              </DropdownField>
            </FiltersGrid>

            <ActiveChipRow>
              {activeChips.map((chip) => (
                <ActiveChip key={chip.key}>
                  <span>{renderChipLabel(chip)}</span>
                  <ActiveChipRemove
                    type="button"
                    aria-label={t("smrtSearch.removeChip")}
                    onClick={() => handleChipRemove(chip.key)}
                  >
                    x
                  </ActiveChipRemove>
                </ActiveChip>
              ))}
            </ActiveChipRow>

            <AdvancedToggle
              ref={advancedToggleRef}
              type="button"
              $active={advancedOpen}
              onClick={() => setAdvancedOpen((previousOpen) => !previousOpen)}
            >
              <AdvancedToggleIcon $active={advancedOpen}>
                {advancedOpen ? "-" : "+"}
              </AdvancedToggleIcon>
              <span>{t("smrtSearch.advancedParameters")}</span>
            </AdvancedToggle>

            <AdvancedBody $expanded={advancedOpen}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(150px, 180px)",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "12px",
                  padding: "12px 22px",
                  border: "0.5px solid var(--border-color, #3a3a3a)",
                  borderRadius: "10px",
                }}
              >
                <FilterLabel htmlFor="smrt-search-global-parameter-mode" style={{ marginBottom: 0 }}>
                  {t("compareWorkspace.globalParameterMode")}
                </FilterLabel>
                <FieldControl
                  id="smrt-search-global-parameter-mode"
                  aria-label={t("compareWorkspace.globalParameterMode")}
                  value={globalAdvancedParamMode}
                  onChange={(event) => handleApplyGlobalAdvancedParamMode(event.target.value)}
                  disabled={!advancedParamCatalog.items.length}
                >
                  <option value="" disabled>
                    {t("compareWorkspace.globalParameterModePlaceholder")}
                  </option>
                  {statModeOptions.map((option) => (
                    <option key={`global-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </FieldControl>
              </div>
              {loadingAdvancedParams ? (
                <LoadingState>{t("compareWorkspace.loading")}</LoadingState>
              ) : advancedParamCatalog.groups.length > 0 ? (
                <>
                  {advancedParamCatalog.groups.map((group) => {
                    const isExpanded = Boolean(manualAdvancedGroups[group.key]);

                    return (
                      <AdvancedParamGroup key={group.key}>
                        <AdvancedParamGroupHeader
                          type="button"
                          onClick={() => handleAdvancedGroupToggle(group.key)}
                        >
                          <AdvancedParamGroupTitle $active={isExpanded}>
                            {group.label}
                          </AdvancedParamGroupTitle>
                          <AdvancedToggleIcon $active={isExpanded} $group>
                            {isExpanded ? "-" : "+"}
                          </AdvancedToggleIcon>
                        </AdvancedParamGroupHeader>
                        <AdvancedParamGroupBody $expanded={isExpanded}>
                          {isExpanded ? (
                            <AdvancedParamGrid>
                              {group.items.map(renderAdvancedParamRow)}
                            </AdvancedParamGrid>
                          ) : null}
                        </AdvancedParamGroupBody>
                      </AdvancedParamGroup>
                    );
                  })}
                </>
              ) : (
                <AdvancedParamEmptyState>
                  {t("smrtSearch.advancedEmpty")}
                </AdvancedParamEmptyState>
              )}
            </AdvancedBody>

            <WorkspaceSearchButtonRow ref={searchActionsRef}>
              <WorkspaceSearchButton
                type="button"
                disabled={loadingSearch}
                onClick={handleSearchSubmit}
              >
                {loadingSearch
                  ? t("compareWorkspace.loading")
                  : t("smrtSearch.searchPlayers")}
              </WorkspaceSearchButton>
            </WorkspaceSearchButtonRow>
          </SmrtSearchPanel>

          {hasSearched ? (
            <ResultsSection>
              {loadingSearch ? (
                <LoadingState>{t("compareWorkspace.loading")}</LoadingState>
              ) : summarizedPlayers.length > 0 ? (
                <>
                  <ResultsHeader ref={resultsHeaderRef}>
                    <ResultsCount>
                      {t("smrtSearch.showingPlayers", { count: totalPlayers })}
                    </ResultsCount>

                    <ResultsSortRow>
                      <ResultsSortLabel>
                        {t("smrtSearch.sortByLabel")}
                      </ResultsSortLabel>
                      <ResultsSortSelect value={sortKey} onChange={handleSortChange}>
                        {SORT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </option>
                        ))}
                      </ResultsSortSelect>
                      <ViewToggleGroup>
                        <ViewToggleButton
                          type="button"
                          $active={viewMode === VIEW_MODES.GRID}
                          onClick={() => setViewMode(VIEW_MODES.GRID)}
                          aria-label={t("smrtSearch.gridView")}
                          aria-pressed={viewMode === VIEW_MODES.GRID}
                        >
                          [][]
                        </ViewToggleButton>
                        <ViewToggleButton
                          type="button"
                          $active={viewMode === VIEW_MODES.LIST}
                          onClick={() => setViewMode(VIEW_MODES.LIST)}
                          aria-label={t("smrtSearch.listView")}
                          aria-pressed={viewMode === VIEW_MODES.LIST}
                        >
                          ===
                        </ViewToggleButton>
                      </ViewToggleGroup>
                    </ResultsSortRow>
                  </ResultsHeader>

                  {viewMode === VIEW_MODES.GRID ? (
                    <ResultsGrid>
                      {summarizedPlayers.map((player) => {
                        const visibleStats = getVisiblePlayerStats(player);
                        const isFavorite = Boolean(favoritePlayers[player.id]);
                        const playerName = player.known_name || player.fullName;
                        const favoriteActionLabel = isFavorite
                          ? t("compareWorkspace.removeFavorite")
                          : t("compareWorkspace.addFavorite");

                        return (
                          <PlayerCard key={player.id}>
                            <PlayerCardTop>
                              <PlayerActionsMenu
                                favorite={isFavorite}
                                onAddToShadowTeam={handleOpenShadowTeam}
                                onCompare={handleComparePlayer}
                                onOpenProfile={handleOpenProfile}
                                onToggleFavorite={handleToggleFavorite}
                                player={player}
                                t={t}
                              />

                              <PlayerCardIdentity>
                                <PlayerIdentityAvatar player={player} />

                                <PlayerCardDetails>
                                  <PlayerCardName>
                                    <PlayerIdentityNameLink
                                      href={buildPlayerPath(player.id)}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {playerName}
                                    </PlayerIdentityNameLink>
                                  </PlayerCardName>
                                  <PlayerIdentityActionGroup>
                                    <PlayerIdentityActionButton
                                      type="button"
                                      aria-label={favoriteActionLabel}
                                      data-tooltip-id={`card-fav-${player.id}`}
                                      onClick={() => handleToggleFavorite(player)}
                                    >
                                      <img
                                        src={isFavorite ? starOn : starOff}
                                        alt=""
                                        aria-hidden="true"
                                      />
                                    </PlayerIdentityActionButton>
                                    <Tooltip id={`card-fav-${player.id}`} place="bottom" content={favoriteActionLabel} />
                                    <PlayerIdentityActionButton
                                      type="button"
                                      aria-label={t("smrtSearch.comparePlayerAction")}
                                      data-tooltip-id={`card-compare-${player.id}`}
                                      onClick={() => handleComparePlayer(player)}
                                    >
                                      <img
                                        src={compareIcon}
                                        alt=""
                                        aria-hidden="true"
                                      />
                                    </PlayerIdentityActionButton>
                                    <Tooltip id={`card-compare-${player.id}`} place="bottom" content={t("smrtSearch.comparePlayerAction")} />
                                    <PlayerIdentityActionButton
                                      type="button"
                                      aria-label={t("smrtSearch.shadowTeamPlayerAction")}
                                      data-tooltip-id={`card-shadow-${player.id}`}
                                      onClick={() => handleOpenShadowTeam(player)}
                                    >
                                      <img
                                        src={localStorage.getItem("theme") === "light" ? shadowTeamIconLight : shadowTeamIconDark}
                                        alt=""
                                        aria-hidden="true"
                                      />
                                    </PlayerIdentityActionButton>
                                    <Tooltip id={`card-shadow-${player.id}`} place="bottom" content={t("smrtSearch.shadowTeamPlayerAction")} />
                                  </PlayerIdentityActionGroup>
                                  <PlayerCardMeta>
                                    {player.nationName ? (
                                      <PlayerMetaText>{player.nationName}</PlayerMetaText>
                                    ) : null}
                                    {player.clubName ? (
                                      player.clubId ? (
                                        <PlayerMetaLink
                                          href={buildTeamPath(player.clubId)}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {player.clubName}
                                        </PlayerMetaLink>
                                      ) : (
                                        <PlayerMetaText>{player.clubName}</PlayerMetaText>
                                      )
                                    ) : null}
                                    <PositionBadge $position={player.positionCode}>
                                      {player.positionLabel || player.positionCode}
                                    </PositionBadge>
                                  </PlayerCardMeta>
                                </PlayerCardDetails>
                              </PlayerCardIdentity>
                            </PlayerCardTop>

                            <PlayerCardStats>
                              {visibleStats.map((stat) => (
                                <PlayerCardStat key={stat.key}>
                                  <PlayerCardStatValue>
                                    {stat.value}
                                  </PlayerCardStatValue>
                                  <PlayerCardStatLabel>
                                    {stat.label}
                                  </PlayerCardStatLabel>
                                </PlayerCardStat>
                              ))}
                            </PlayerCardStats>

                            <SmrtIndexBarRow>
                              <SmrtIndexLabel>
                                {t("smrtSearch.sortSmrtIndex")}
                              </SmrtIndexLabel>
                              <SmrtIndexTrack>
                                <SmrtIndexFill
                                  $width={`${Math.max(
                                    0,
                                    Math.min(100, Number(player.smrtIndex || 0))
                                  )}%`}
                                />
                              </SmrtIndexTrack>
                              <SmrtIndexValue>
                                {formatInteger(player.smrtIndex)}
                              </SmrtIndexValue>
                            </SmrtIndexBarRow>
                          </PlayerCard>
                        );
                      })}
                    </ResultsGrid>
                  ) : (
                    <ResultsList>
                      {summarizedPlayers.map((player) => {
                        const visibleStats = getVisiblePlayerStats(player, {
                          compact: true,
                        });
                        const isFavorite = Boolean(favoritePlayers[player.id]);
                        const playerName = player.known_name || player.fullName;
                        const favoriteActionLabel = isFavorite
                          ? t("compareWorkspace.removeFavorite")
                          : t("compareWorkspace.addFavorite");

                        return (
                          <PlayerListRow key={player.id}>
                            <PlayerIdentityAvatar player={player} compact />

                            <PlayerListInfo>
                              <PlayerListName>
                                <PlayerIdentityActionGroup>
                                  <PlayerIdentityActionButton
                                    type="button"
                                    aria-label={favoriteActionLabel}
                                    data-tooltip-id={`list-fav-${player.id}`}
                                    onClick={() => handleToggleFavorite(player)}
                                  >
                                    <img
                                      src={isFavorite ? starOn : starOff}
                                      alt=""
                                      aria-hidden="true"
                                    />
                                  </PlayerIdentityActionButton>
                                  <Tooltip id={`list-fav-${player.id}`} place="bottom" content={favoriteActionLabel} />
                                  <PlayerIdentityActionButton
                                    type="button"
                                    aria-label={t("smrtSearch.comparePlayerAction")}
                                    data-tooltip-id={`list-compare-${player.id}`}
                                    onClick={() => handleComparePlayer(player)}
                                  >
                                    <img
                                      src={compareIcon}
                                      alt=""
                                      aria-hidden="true"
                                    />
                                  </PlayerIdentityActionButton>
                                  <Tooltip id={`list-compare-${player.id}`} place="bottom" content={t("smrtSearch.comparePlayerAction")} />
                                  <PlayerIdentityActionButton
                                    type="button"
                                    aria-label={t("smrtSearch.shadowTeamPlayerAction")}
                                    data-tooltip-id={`list-shadow-${player.id}`}
                                    onClick={() => handleOpenShadowTeam(player)}
                                  >
                                    <img
                                      src={localStorage.getItem("theme") === "light" ? shadowTeamIconLight : shadowTeamIconDark}
                                      alt=""
                                      aria-hidden="true"
                                    />
                                  </PlayerIdentityActionButton>
                                  <Tooltip id={`list-shadow-${player.id}`} place="bottom" content={t("smrtSearch.shadowTeamPlayerAction")} />
                                  <PlayerIdentityNameLink
                                    href={buildPlayerPath(player.id)}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {playerName}
                                  </PlayerIdentityNameLink>
                                </PlayerIdentityActionGroup>
                                <PositionBadge $position={player.positionCode}>
                                  {player.positionLabel || player.positionCode}
                                </PositionBadge>
                              </PlayerListName>
                              <PlayerListMeta>
                                <PlayerMetaSummary
                                  player={player}
                                  ageLabel={t("compareWorkspace.filterAge")}
                                />
                              </PlayerListMeta>
                            </PlayerListInfo>

                            <PlayerListStats>
                              {visibleStats.map((stat) => (
                                <PlayerCardStat key={stat.key}>
                                  <PlayerCardStatValue>
                                    {stat.value}
                                  </PlayerCardStatValue>
                                  <PlayerCardStatLabel>
                                    {stat.label}
                                  </PlayerCardStatLabel>
                                </PlayerCardStat>
                              ))}
                            </PlayerListStats>

                            <PlayerListSmrt>
                              <SmrtIndexTrack>
                                <SmrtIndexFill
                                  $width={`${Math.max(
                                    0,
                                    Math.min(100, Number(player.smrtIndex || 0))
                                  )}%`}
                                />
                              </SmrtIndexTrack>
                              <SmrtIndexValue>
                                {formatInteger(player.smrtIndex)}
                              </SmrtIndexValue>
                            </PlayerListSmrt>

                            <PlayerActionsMenu
                              favorite={isFavorite}
                              onAddToShadowTeam={handleOpenShadowTeam}
                              onCompare={handleComparePlayer}
                              onOpenProfile={handleOpenProfile}
                              onToggleFavorite={handleToggleFavorite}
                              player={player}
                              t={t}
                              style={{
                                position: "relative",
                                top: "auto",
                                right: "auto",
                                marginLeft: "auto",
                              }}
                            />
                          </PlayerListRow>
                        );
                      })}
                    </ResultsList>
                  )}

                  <PaginationRow>
                    <PaginationButton
                      type="button"
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                    >
                      {"<"}
                    </PaginationButton>

                    {paginationItems.map((item, index) =>
                      item === "..." ? (
                        <PaginationEllipsis key={`ellipsis-${index}`}>
                          ...
                        </PaginationEllipsis>
                      ) : (
                        <PaginationButton
                          key={item}
                          type="button"
                          $active={item === page}
                          onClick={() => handlePageChange(item)}
                        >
                          {item}
                        </PaginationButton>
                      )
                    )}

                    <PaginationButton
                      type="button"
                      disabled={page === totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    >
                      {">"}
                    </PaginationButton>
                  </PaginationRow>
                </>
              ) : (
                <WorkspaceEmptyState>
                  <img src={emptySearch} alt="" />
                  <h3>{t("smrtSearch.emptySearchTitle")}</h3>
                  <p>{t("compareWorkspace.noPlayerSearchResults")}</p>
                </WorkspaceEmptyState>
              )}
            </ResultsSection>
          ) : null}

          <TemplateToast $visible={Boolean(toastMessage)}>
            {toastMessage}
          </TemplateToast>
        </SmrtSearchWorkspace>

        {!showTutorial && (
          <>
            <div aria-hidden="true" style={{ height: 72 }} />
            <TutorialButton
              $bottom={tutorialHelpBottom}
              type="button"
              onClick={handleTutorialReplay}
            >
              {t("smrtSearch.tutorialHelp")}
            </TutorialButton>
          </>
        )}

        {showTutorial && tutorialCurrentStep && (
          <>
            {tutorialCurrentStep.spotlight && tutorialRect ? (
              <svg
                style={{
                  position: "fixed",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "auto",
                  zIndex: 50,
                }}
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <mask id="smrt-search-tutorial-spotlight-mask">
                    <rect width="100%" height="100%" fill="white" />
                    <rect
                      x={tutorialRect.left - tutorialSpotlightPadding}
                      y={tutorialRect.top - tutorialSpotlightPadding}
                      width={tutorialRect.width + tutorialSpotlightPadding * 2}
                      height={tutorialRect.height + tutorialSpotlightPadding * 2}
                      rx="12"
                      fill="black"
                    />
                  </mask>
                </defs>
                <rect
                  width="100%"
                  height="100%"
                  fill="rgba(0,0,0,0.78)"
                  mask="url(#smrt-search-tutorial-spotlight-mask)"
                />
              </svg>
            ) : (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: _isLightTheme ? "rgba(15,17,26,0.55)" : "rgba(0,0,0,0.82)",
                  zIndex: 50,
                  pointerEvents: "auto",
                }}
              />
            )}

            {tutorialSpotlightStyle && (
              <div
                style={{
                  position: "fixed",
                  ...tutorialSpotlightStyle,
                  borderRadius: 14,
                  border: `2px solid ${SMRT_SEARCH_TUTORIAL_ACCENT}`,
                  boxShadow: `0 0 0 1px ${SMRT_SEARCH_TUTORIAL_ACCENT}44, 0 0 24px ${SMRT_SEARCH_TUTORIAL_ACCENT}66, 0 0 48px ${SMRT_SEARCH_TUTORIAL_ACCENT}22`,
                  zIndex: 51,
                  pointerEvents: "none",
                  animation: "smrtSearchTutorialPulse 2s ease-in-out infinite",
                }}
              />
            )}

            <div
              key={tutorialCurrentStep.id}
              style={{
                ...tutorialCardStyle,
                zIndex: 60,
                background: SMRT_SEARCH_TUTORIAL_CARD_BG,
                border: _isLightTheme ? "1px solid #E5E7EB" : `1px solid ${SMRT_SEARCH_TUTORIAL_ACCENT}33`,
                borderRadius: 20,
                padding: "28px 28px 22px",
                boxShadow: _isLightTheme
                  ? "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
                  : `0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px ${SMRT_SEARCH_TUTORIAL_ACCENT}22, inset 0 1px 0 ${SMRT_SEARCH_TUTORIAL_ACCENT}11`,
                fontFamily: SMRT_SEARCH_TUTORIAL_FONT_BASE,
              }}
            >
              {tutorialCurrentStep.id !== "welcome" &&
                tutorialRect &&
                tutorialCurrentStep.position === "below" && (
                  <div
                    style={{
                      position: "absolute",
                      top: -10,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 0,
                      height: 0,
                      borderLeft: "10px solid transparent",
                      borderRight: "10px solid transparent",
                      borderBottom: `10px solid ${SMRT_SEARCH_TUTORIAL_ACCENT}55`,
                    }}
                  />
              )}
              {tutorialCurrentStep.id !== "welcome" &&
                tutorialRect &&
                tutorialCurrentStep.position === "above" && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: -10,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 0,
                      height: 0,
                      borderLeft: "10px solid transparent",
                      borderRight: "10px solid transparent",
                      borderTop: `10px solid ${SMRT_SEARCH_TUTORIAL_ACCENT}55`,
                    }}
                  />
              )}

              <div
                style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}
              >
                {smrtSearchTutorialSteps.map((stepItem, index) => (
                  <div
                    key={stepItem.id}
                    style={{
                      height: 3,
                      flex: 1,
                      borderRadius: 99,
                      background:
                        index <= tutorialStep
                          ? SMRT_SEARCH_TUTORIAL_ACCENT
                          : SMRT_SEARCH_TUTORIAL_STEP_INACTIVE,
                      transition: "background 0.4s",
                    }}
                  />
                ))}
              </div>

              {tutorialCurrentStep.id === "welcome" && (
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: `linear-gradient(135deg, ${SMRT_SEARCH_TUTORIAL_ACCENT}33, ${SMRT_SEARCH_TUTORIAL_ACCENT}11)`,
                    border: `1px solid ${SMRT_SEARCH_TUTORIAL_ACCENT}55`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 24,
                    marginBottom: 16,
                  }}
                >
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      filter:
                        "drop-shadow(0 0 4px rgba(255, 218, 0, 0.55)) drop-shadow(0 0 8px rgba(174, 119, 20, 0.35))",
                    }}
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient
                        id="smrtSearchTutorialBoltGradient"
                        x1="15.5"
                        y1="1.5"
                        x2="6.5"
                        y2="22.5"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0%" stopColor="#FFDA00" />
                        <stop offset="100%" stopColor="#AE7714" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M13.8 1.5L5.5 11.8h5.5L9.9 22.5l8.6-12h-5L13.8 1.5z"
                      fill="url(#smrtSearchTutorialBoltGradient)"
                      stroke="#FFF4B3"
                      strokeWidth="0.4"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}

              <h2
                style={{
                  margin: "0 0 10px",
                  fontSize: tutorialCurrentStep.id === "welcome" ? 22 : 18,
                  fontWeight: 800,
                  fontFamily: SMRT_SEARCH_TUTORIAL_FONT_BOLD,
                  color: SMRT_SEARCH_TUTORIAL_TITLE_COLOR,
                  lineHeight: 1.25,
                  whiteSpace: "pre-line",
                  letterSpacing: -0.3,
                }}
              >
                {tutorialCurrentStep.title}
              </h2>

              <p
                style={{
                  margin: "0 0 24px",
                  fontSize: 13.5,
                  fontFamily: SMRT_SEARCH_TUTORIAL_FONT_BASE,
                  color: SMRT_SEARCH_TUTORIAL_BODY_COLOR,
                  lineHeight: 1.6,
                }}
              >
                {tutorialCurrentStep.body}
              </p>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <button
                  type="button"
                  onClick={handleTutorialSkip}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: SMRT_SEARCH_TUTORIAL_SKIP_COLOR,
                    fontSize: 13,
                    cursor: "pointer",
                    padding: "6px 0",
                    fontFamily: SMRT_SEARCH_TUTORIAL_FONT_MEDIUM,
                    fontWeight: 500,
                    textDecoration: "underline",
                    textUnderlineOffset: 3,
                  }}
                >
                  {t("smrtSearch.tutorialSkip")}
                </button>

                <button
                  type="button"
                  onClick={handleTutorialNext}
                  style={{
                    background: SMRT_SEARCH_TUTORIAL_ACCENT,
                    border: "none",
                    color: SMRT_SEARCH_TUTORIAL_BTN_TEXT,
                    fontFamily: SMRT_SEARCH_TUTORIAL_FONT_BOLD,
                    fontWeight: 800,
                    fontSize: 13.5,
                    padding: "10px 24px",
                    borderRadius: 10,
                    cursor: "pointer",
                    letterSpacing: 0.3,
                    boxShadow: `0 4px 20px ${SMRT_SEARCH_TUTORIAL_ACCENT}55`,
                  }}
                >
                  {tutorialIsLastStep
                    ? t("smrtSearch.tutorialDone")
                    : t("smrtSearch.tutorialNext")}
                </button>
              </div>
            </div>

            <style>
              {`
                @keyframes smrtSearchTutorialPulse {
                  0%,100% { box-shadow: 0 0 0 1px ${SMRT_SEARCH_TUTORIAL_ACCENT}44, 0 0 20px ${SMRT_SEARCH_TUTORIAL_ACCENT}55, 0 0 40px ${SMRT_SEARCH_TUTORIAL_ACCENT}22; }
                  50% { box-shadow: 0 0 0 2px ${SMRT_SEARCH_TUTORIAL_ACCENT}88, 0 0 32px ${SMRT_SEARCH_TUTORIAL_ACCENT}99, 0 0 64px ${SMRT_SEARCH_TUTORIAL_ACCENT}44; }
                }
              `}
            </style>
          </>
        )}

        <Modal
          isOpen={shadowTeamModalOpen}
          setIsOpen={handleCloseShadowTeamModal}
          closeIc
          width="560px"
          mobileWidth="calc(100vw - 24px)"
          mobilePadding="72px 0 28px"
          padding="withClose"
          unstyledTypography
        >
          <ShadowTeamModalShell>
            <ShadowTeamModalTitle>
              {t("smrtSearch.shadowTeamModalTitle")}
            </ShadowTeamModalTitle>
            <ShadowTeamModalSubtitle>
              {t("smrtSearch.shadowTeamModalSubtitle", {
                name: shadowTeamModalPlayerName,
              })}
            </ShadowTeamModalSubtitle>

            <ShadowTeamModalBody>
              {!selectedShadowTeam ? (
                <>
                  <ShadowTeamModalStepTitle>
                    {t("smrtSearch.shadowTeamSelectTeamStep")}
                  </ShadowTeamModalStepTitle>

                  {shadowTeamLoadingTeams ? (
                    <LoadingState>{t("compareWorkspace.loading")}</LoadingState>
                  ) : shadowTeams.length > 0 ? (
                    <ShadowTeamModalSection>
                      {shadowTeams.map((team) => (
                        <ShadowTeamModalItemButton
                          key={team.id}
                          type="button"
                          onClick={() => handleSelectShadowTeam(team)}
                        >
                          <ShadowTeamModalItemText>
                            <ShadowTeamModalItemTitle>
                              {team.name}
                            </ShadowTeamModalItemTitle>
                            <ShadowTeamModalItemMeta>
                              {team.formationName || team.formation?.name || ""}
                            </ShadowTeamModalItemMeta>
                          </ShadowTeamModalItemText>
                          <span aria-hidden="true">›</span>
                        </ShadowTeamModalItemButton>
                      ))}
                    </ShadowTeamModalSection>
                  ) : (
                    <ShadowTeamModalEmptyState>
                      {t("smrtSearch.shadowTeamNoTeamsMessage")}
                    </ShadowTeamModalEmptyState>
                  )}
                </>
              ) : (
                <>
                  <ShadowTeamModalBack
                    type="button"
                    onClick={() => {
                      setShadowTeamSelectedTeamId(null);
                      setShadowTeamLoadingTeamId(null);
                    }}
                  >
                    ‹ {t("smrtSearch.shadowTeamBack")}
                  </ShadowTeamModalBack>

                  <ShadowTeamModalSection>
                    <ShadowTeamModalStepTitle>
                      {t("smrtSearch.shadowTeamSelectPositionStep")}
                    </ShadowTeamModalStepTitle>
                    <ShadowTeamModalItemMeta>
                      {selectedShadowTeam.name}
                      {selectedShadowTeam.formationName ||
                      selectedShadowTeam.formation?.name
                        ? ` · ${
                            selectedShadowTeam.formationName ||
                            selectedShadowTeam.formation?.name
                          }`
                        : ""}
                    </ShadowTeamModalItemMeta>
                  </ShadowTeamModalSection>

                  {isShadowTeamDetailLoading ? (
                    <LoadingState>{t("compareWorkspace.loading")}</LoadingState>
                  ) : null}

                  {getShadowTeamSlotGroups(selectedShadowTeam.formation).map((group) => (
                    <ShadowTeamSlotSection key={group.lineIndex}>
                      <ShadowTeamSlotSectionLabel>
                        {SHADOW_TEAM_BAND_LABELS[group.band] || group.band}
                      </ShadowTeamSlotSectionLabel>
                      <ShadowTeamSlotGrid>
                        {group.slots.map((slot) => (
                          <ShadowTeamSlotButton
                            key={slot.key}
                            type="button"
                            disabled={
                              shadowTeamAddingPlayer ||
                              isShadowTeamDetailLoading ||
                              !selectedShadowTeamHasLoadedAssignments ||
                              isShadowTeamSlotFull(slot)
                            }
                            onClick={() => handleAddToShadowTeamSlot(slot)}
                          >
                            {slot.shortLabel}
                          </ShadowTeamSlotButton>
                        ))}
                      </ShadowTeamSlotGrid>
                    </ShadowTeamSlotSection>
                  ))}
                </>
              )}
            </ShadowTeamModalBody>
          </ShadowTeamModalShell>
        </Modal>

        <NotificationList notifications={notifications} onClose={removeNotification} />
      </MainWrapperFavorite>
    </MainWrapper>
  );
};

export default Index;
